(function () {
  'use strict';

  /**
   * --------------------------------------------------------------------------
   * Bootstrap (v5.1.3): util/index.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */
  const MILLISECONDS_MULTIPLIER = 1000;
  const TRANSITION_END = 'transitionend';

  // Shoutout AngusCroll (https://goo.gl/pxwQGp)
  const toType = obj => {
    if (obj === null || obj === undefined) {
      return `${obj}`
    }

    return {}.toString.call(obj).match(/\s([a-z]+)/i)[1].toLowerCase()
  };

  const getSelector = element => {
    let selector = element.getAttribute('data-bs-target');

    if (!selector || selector === '#') {
      let hrefAttr = element.getAttribute('href');

      // The only valid content that could double as a selector are IDs or classes,
      // so everything starting with `#` or `.`. If a "real" URL is used as the selector,
      // `document.querySelector` will rightfully complain it is invalid.
      // See https://github.com/twbs/bootstrap/issues/32273
      if (!hrefAttr || (!hrefAttr.includes('#') && !hrefAttr.startsWith('.'))) {
        return null
      }

      // Just in case some CMS puts out a full URL with the anchor appended
      if (hrefAttr.includes('#') && !hrefAttr.startsWith('#')) {
        hrefAttr = `#${hrefAttr.split('#')[1]}`;
      }

      selector = hrefAttr && hrefAttr !== '#' ? hrefAttr.trim() : null;
    }

    return selector
  };

  const getElementFromSelector = element => {
    const selector = getSelector(element);

    return selector ? document.querySelector(selector) : null
  };

  const getTransitionDurationFromElement = element => {
    if (!element) {
      return 0
    }

    // Get transition-duration of the element
    let { transitionDuration, transitionDelay } = window.getComputedStyle(element);

    const floatTransitionDuration = Number.parseFloat(transitionDuration);
    const floatTransitionDelay = Number.parseFloat(transitionDelay);

    // Return 0 if element or transition duration is not found
    if (!floatTransitionDuration && !floatTransitionDelay) {
      return 0
    }

    // If multiple durations are defined, take the first
    transitionDuration = transitionDuration.split(',')[0];
    transitionDelay = transitionDelay.split(',')[0];

    return (Number.parseFloat(transitionDuration) + Number.parseFloat(transitionDelay)) * MILLISECONDS_MULTIPLIER
  };

  const triggerTransitionEnd = element => {
    element.dispatchEvent(new Event(TRANSITION_END));
  };

  const isElement = obj => {
    if (!obj || typeof obj !== 'object') {
      return false
    }

    if (typeof obj.jquery !== 'undefined') {
      obj = obj[0];
    }

    return typeof obj.nodeType !== 'undefined'
  };

  const getElement = obj => {
    if (isElement(obj)) { // it's a jQuery object or a node element
      return obj.jquery ? obj[0] : obj
    }

    if (typeof obj === 'string' && obj.length > 0) {
      return document.querySelector(obj)
    }

    return null
  };

  const typeCheckConfig = (componentName, config, configTypes) => {
    Object.keys(configTypes).forEach(property => {
      const expectedTypes = configTypes[property];
      const value = config[property];
      const valueType = value && isElement(value) ? 'element' : toType(value);

      if (!new RegExp(expectedTypes).test(valueType)) {
        throw new TypeError(
          `${componentName.toUpperCase()}: Option "${property}" provided type "${valueType}" but expected type "${expectedTypes}".`
        )
      }
    });
  };

  const isVisible = element => {
    if (!isElement(element) || element.getClientRects().length === 0) {
      return false
    }

    return getComputedStyle(element).getPropertyValue('visibility') === 'visible'
  };

  const isDisabled = element => {
    if (!element || element.nodeType !== Node.ELEMENT_NODE) {
      return true
    }

    if (element.classList.contains('disabled')) {
      return true
    }

    if (typeof element.disabled !== 'undefined') {
      return element.disabled
    }

    return element.hasAttribute('disabled') && element.getAttribute('disabled') !== 'false'
  };

  /**
   * Trick to restart an element's animation
   *
   * @param {HTMLElement} element
   * @return void
   *
   * @see https://www.charistheo.io/blog/2021/02/restart-a-css-animation-with-javascript/#restarting-a-css-animation
   */
  const reflow = element => {
    // eslint-disable-next-line no-unused-expressions
    element.offsetHeight;
  };

  const getjQuery = () => {
    const { jQuery } = window;

    if (jQuery && !document.body.hasAttribute('data-bs-no-jquery')) {
      return jQuery
    }

    return null
  };

  const DOMContentLoadedCallbacks = [];

  const onDOMContentLoaded = callback => {
    if (document.readyState === 'loading') {
      // add listener on the first call when the document is in loading state
      if (!DOMContentLoadedCallbacks.length) {
        document.addEventListener('DOMContentLoaded', () => {
          DOMContentLoadedCallbacks.forEach(callback => callback());
        });
      }

      DOMContentLoadedCallbacks.push(callback);
    } else {
      callback();
    }
  };

  const isRTL = () => document.documentElement.dir === 'rtl';

  const defineJQueryPlugin = plugin => {
    onDOMContentLoaded(() => {
      const $ = getjQuery();
      /* istanbul ignore if */
      if ($) {
        const name = plugin.NAME;
        const JQUERY_NO_CONFLICT = $.fn[name];
        $.fn[name] = plugin.jQueryInterface;
        $.fn[name].Constructor = plugin;
        $.fn[name].noConflict = () => {
          $.fn[name] = JQUERY_NO_CONFLICT;
          return plugin.jQueryInterface
        };
      }
    });
  };

  const execute = callback => {
    if (typeof callback === 'function') {
      callback();
    }
  };

  const executeAfterTransition = (callback, transitionElement, waitForTransition = true) => {
    if (!waitForTransition) {
      execute(callback);
      return
    }

    const durationPadding = 5;
    const emulatedDuration = getTransitionDurationFromElement(transitionElement) + durationPadding;

    let called = false;

    const handler = ({ target }) => {
      if (target !== transitionElement) {
        return
      }

      called = true;
      transitionElement.removeEventListener(TRANSITION_END, handler);
      execute(callback);
    };

    transitionElement.addEventListener(TRANSITION_END, handler);
    setTimeout(() => {
      if (!called) {
        triggerTransitionEnd(transitionElement);
      }
    }, emulatedDuration);
  };

  /**
   * --------------------------------------------------------------------------
   * Bootstrap (v5.1.3): dom/event-handler.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  const namespaceRegex = /[^.]*(?=\..*)\.|.*/;
  const stripNameRegex = /\..*/;
  const stripUidRegex = /::\d+$/;
  const eventRegistry = {}; // Events storage
  let uidEvent = 1;
  const customEvents = {
    mouseenter: 'mouseover',
    mouseleave: 'mouseout'
  };
  const customEventsRegex = /^(mouseenter|mouseleave)/i;
  const nativeEvents = new Set([
    'click',
    'dblclick',
    'mouseup',
    'mousedown',
    'contextmenu',
    'mousewheel',
    'DOMMouseScroll',
    'mouseover',
    'mouseout',
    'mousemove',
    'selectstart',
    'selectend',
    'keydown',
    'keypress',
    'keyup',
    'orientationchange',
    'touchstart',
    'touchmove',
    'touchend',
    'touchcancel',
    'pointerdown',
    'pointermove',
    'pointerup',
    'pointerleave',
    'pointercancel',
    'gesturestart',
    'gesturechange',
    'gestureend',
    'focus',
    'blur',
    'change',
    'reset',
    'select',
    'submit',
    'focusin',
    'focusout',
    'load',
    'unload',
    'beforeunload',
    'resize',
    'move',
    'DOMContentLoaded',
    'readystatechange',
    'error',
    'abort',
    'scroll'
  ]);

  /**
   * ------------------------------------------------------------------------
   * Private methods
   * ------------------------------------------------------------------------
   */

  function getUidEvent(element, uid) {
    return (uid && `${uid}::${uidEvent++}`) || element.uidEvent || uidEvent++
  }

  function getEvent(element) {
    const uid = getUidEvent(element);

    element.uidEvent = uid;
    eventRegistry[uid] = eventRegistry[uid] || {};

    return eventRegistry[uid]
  }

  function bootstrapHandler(element, fn) {
    return function handler(event) {
      event.delegateTarget = element;

      if (handler.oneOff) {
        EventHandler.off(element, event.type, fn);
      }

      return fn.apply(element, [event])
    }
  }

  function bootstrapDelegationHandler(element, selector, fn) {
    return function handler(event) {
      const domElements = element.querySelectorAll(selector);

      for (let { target } = event; target && target !== this; target = target.parentNode) {
        for (let i = domElements.length; i--;) {
          if (domElements[i] === target) {
            event.delegateTarget = target;

            if (handler.oneOff) {
              EventHandler.off(element, event.type, selector, fn);
            }

            return fn.apply(target, [event])
          }
        }
      }

      // To please ESLint
      return null
    }
  }

  function findHandler(events, handler, delegationSelector = null) {
    const uidEventList = Object.keys(events);

    for (let i = 0, len = uidEventList.length; i < len; i++) {
      const event = events[uidEventList[i]];

      if (event.originalHandler === handler && event.delegationSelector === delegationSelector) {
        return event
      }
    }

    return null
  }

  function normalizeParams(originalTypeEvent, handler, delegationFn) {
    const delegation = typeof handler === 'string';
    const originalHandler = delegation ? delegationFn : handler;

    let typeEvent = getTypeEvent(originalTypeEvent);
    const isNative = nativeEvents.has(typeEvent);

    if (!isNative) {
      typeEvent = originalTypeEvent;
    }

    return [delegation, originalHandler, typeEvent]
  }

  function addHandler(element, originalTypeEvent, handler, delegationFn, oneOff) {
    if (typeof originalTypeEvent !== 'string' || !element) {
      return
    }

    if (!handler) {
      handler = delegationFn;
      delegationFn = null;
    }

    // in case of mouseenter or mouseleave wrap the handler within a function that checks for its DOM position
    // this prevents the handler from being dispatched the same way as mouseover or mouseout does
    if (customEventsRegex.test(originalTypeEvent)) {
      const wrapFn = fn => {
        return function (event) {
          if (!event.relatedTarget || (event.relatedTarget !== event.delegateTarget && !event.delegateTarget.contains(event.relatedTarget))) {
            return fn.call(this, event)
          }
        }
      };

      if (delegationFn) {
        delegationFn = wrapFn(delegationFn);
      } else {
        handler = wrapFn(handler);
      }
    }

    const [delegation, originalHandler, typeEvent] = normalizeParams(originalTypeEvent, handler, delegationFn);
    const events = getEvent(element);
    const handlers = events[typeEvent] || (events[typeEvent] = {});
    const previousFn = findHandler(handlers, originalHandler, delegation ? handler : null);

    if (previousFn) {
      previousFn.oneOff = previousFn.oneOff && oneOff;

      return
    }

    const uid = getUidEvent(originalHandler, originalTypeEvent.replace(namespaceRegex, ''));
    const fn = delegation ?
      bootstrapDelegationHandler(element, handler, delegationFn) :
      bootstrapHandler(element, handler);

    fn.delegationSelector = delegation ? handler : null;
    fn.originalHandler = originalHandler;
    fn.oneOff = oneOff;
    fn.uidEvent = uid;
    handlers[uid] = fn;

    element.addEventListener(typeEvent, fn, delegation);
  }

  function removeHandler(element, events, typeEvent, handler, delegationSelector) {
    const fn = findHandler(events[typeEvent], handler, delegationSelector);

    if (!fn) {
      return
    }

    element.removeEventListener(typeEvent, fn, Boolean(delegationSelector));
    delete events[typeEvent][fn.uidEvent];
  }

  function removeNamespacedHandlers(element, events, typeEvent, namespace) {
    const storeElementEvent = events[typeEvent] || {};

    Object.keys(storeElementEvent).forEach(handlerKey => {
      if (handlerKey.includes(namespace)) {
        const event = storeElementEvent[handlerKey];

        removeHandler(element, events, typeEvent, event.originalHandler, event.delegationSelector);
      }
    });
  }

  function getTypeEvent(event) {
    // allow to get the native events from namespaced events ('click.bs.button' --> 'click')
    event = event.replace(stripNameRegex, '');
    return customEvents[event] || event
  }

  const EventHandler = {
    on(element, event, handler, delegationFn) {
      addHandler(element, event, handler, delegationFn, false);
    },

    one(element, event, handler, delegationFn) {
      addHandler(element, event, handler, delegationFn, true);
    },

    off(element, originalTypeEvent, handler, delegationFn) {
      if (typeof originalTypeEvent !== 'string' || !element) {
        return
      }

      const [delegation, originalHandler, typeEvent] = normalizeParams(originalTypeEvent, handler, delegationFn);
      const inNamespace = typeEvent !== originalTypeEvent;
      const events = getEvent(element);
      const isNamespace = originalTypeEvent.startsWith('.');

      if (typeof originalHandler !== 'undefined') {
        // Simplest case: handler is passed, remove that listener ONLY.
        if (!events || !events[typeEvent]) {
          return
        }

        removeHandler(element, events, typeEvent, originalHandler, delegation ? handler : null);
        return
      }

      if (isNamespace) {
        Object.keys(events).forEach(elementEvent => {
          removeNamespacedHandlers(element, events, elementEvent, originalTypeEvent.slice(1));
        });
      }

      const storeElementEvent = events[typeEvent] || {};
      Object.keys(storeElementEvent).forEach(keyHandlers => {
        const handlerKey = keyHandlers.replace(stripUidRegex, '');

        if (!inNamespace || originalTypeEvent.includes(handlerKey)) {
          const event = storeElementEvent[keyHandlers];

          removeHandler(element, events, typeEvent, event.originalHandler, event.delegationSelector);
        }
      });
    },

    trigger(element, event, args) {
      if (typeof event !== 'string' || !element) {
        return null
      }

      const $ = getjQuery();
      const typeEvent = getTypeEvent(event);
      const inNamespace = event !== typeEvent;
      const isNative = nativeEvents.has(typeEvent);

      let jQueryEvent;
      let bubbles = true;
      let nativeDispatch = true;
      let defaultPrevented = false;
      let evt = null;

      if (inNamespace && $) {
        jQueryEvent = $.Event(event, args);

        $(element).trigger(jQueryEvent);
        bubbles = !jQueryEvent.isPropagationStopped();
        nativeDispatch = !jQueryEvent.isImmediatePropagationStopped();
        defaultPrevented = jQueryEvent.isDefaultPrevented();
      }

      if (isNative) {
        evt = document.createEvent('HTMLEvents');
        evt.initEvent(typeEvent, bubbles, true);
      } else {
        evt = new CustomEvent(event, {
          bubbles,
          cancelable: true
        });
      }

      // merge custom information in our event
      if (typeof args !== 'undefined') {
        Object.keys(args).forEach(key => {
          Object.defineProperty(evt, key, {
            get() {
              return args[key]
            }
          });
        });
      }

      if (defaultPrevented) {
        evt.preventDefault();
      }

      if (nativeDispatch) {
        element.dispatchEvent(evt);
      }

      if (evt.defaultPrevented && typeof jQueryEvent !== 'undefined') {
        jQueryEvent.preventDefault();
      }

      return evt
    }
  };

  /**
   * --------------------------------------------------------------------------
   * Bootstrap (v5.1.3): dom/manipulator.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  function normalizeData(val) {
    if (val === 'true') {
      return true
    }

    if (val === 'false') {
      return false
    }

    if (val === Number(val).toString()) {
      return Number(val)
    }

    if (val === '' || val === 'null') {
      return null
    }

    return val
  }

  function normalizeDataKey(key) {
    return key.replace(/[A-Z]/g, chr => `-${chr.toLowerCase()}`)
  }

  const Manipulator = {
    setDataAttribute(element, key, value) {
      element.setAttribute(`data-bs-${normalizeDataKey(key)}`, value);
    },

    removeDataAttribute(element, key) {
      element.removeAttribute(`data-bs-${normalizeDataKey(key)}`);
    },

    getDataAttributes(element) {
      if (!element) {
        return {}
      }

      const attributes = {};

      Object.keys(element.dataset)
        .filter(key => key.startsWith('bs'))
        .forEach(key => {
          let pureKey = key.replace(/^bs/, '');
          pureKey = pureKey.charAt(0).toLowerCase() + pureKey.slice(1, pureKey.length);
          attributes[pureKey] = normalizeData(element.dataset[key]);
        });

      return attributes
    },

    getDataAttribute(element, key) {
      return normalizeData(element.getAttribute(`data-bs-${normalizeDataKey(key)}`))
    },

    offset(element) {
      const rect = element.getBoundingClientRect();

      return {
        top: rect.top + window.pageYOffset,
        left: rect.left + window.pageXOffset
      }
    },

    position(element) {
      return {
        top: element.offsetTop,
        left: element.offsetLeft
      }
    }
  };

  /**
   * --------------------------------------------------------------------------
   * Bootstrap (v5.1.3): dom/selector-engine.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  const NODE_TEXT = 3;

  const SelectorEngine = {
    find(selector, element = document.documentElement) {
      return [].concat(...Element.prototype.querySelectorAll.call(element, selector))
    },

    findOne(selector, element = document.documentElement) {
      return Element.prototype.querySelector.call(element, selector)
    },

    children(element, selector) {
      return [].concat(...element.children)
        .filter(child => child.matches(selector))
    },

    parents(element, selector) {
      const parents = [];

      let ancestor = element.parentNode;

      while (ancestor && ancestor.nodeType === Node.ELEMENT_NODE && ancestor.nodeType !== NODE_TEXT) {
        if (ancestor.matches(selector)) {
          parents.push(ancestor);
        }

        ancestor = ancestor.parentNode;
      }

      return parents
    },

    prev(element, selector) {
      let previous = element.previousElementSibling;

      while (previous) {
        if (previous.matches(selector)) {
          return [previous]
        }

        previous = previous.previousElementSibling;
      }

      return []
    },

    next(element, selector) {
      let next = element.nextElementSibling;

      while (next) {
        if (next.matches(selector)) {
          return [next]
        }

        next = next.nextElementSibling;
      }

      return []
    },

    focusableChildren(element) {
      const focusables = [
        'a',
        'button',
        'input',
        'textarea',
        'select',
        'details',
        '[tabindex]',
        '[contenteditable="true"]'
      ].map(selector => `${selector}:not([tabindex^="-"])`).join(', ');

      return this.find(focusables, element).filter(el => !isDisabled(el) && isVisible(el))
    }
  };

  /**
   * --------------------------------------------------------------------------
   * Bootstrap (v5.1.3): util/scrollBar.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  const SELECTOR_FIXED_CONTENT = '.fixed-top, .fixed-bottom, .is-fixed, .sticky-top';
  const SELECTOR_STICKY_CONTENT = '.sticky-top';

  class ScrollBarHelper {
    constructor() {
      this._element = document.body;
    }

    getWidth() {
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/innerWidth#usage_notes
      const documentWidth = document.documentElement.clientWidth;
      return Math.abs(window.innerWidth - documentWidth)
    }

    hide() {
      const width = this.getWidth();
      this._disableOverFlow();
      // give padding to element to balance the hidden scrollbar width
      this._setElementAttributes(this._element, 'paddingRight', calculatedValue => calculatedValue + width);
      // trick: We adjust positive paddingRight and negative marginRight to sticky-top elements to keep showing fullwidth
      this._setElementAttributes(SELECTOR_FIXED_CONTENT, 'paddingRight', calculatedValue => calculatedValue + width);
      this._setElementAttributes(SELECTOR_STICKY_CONTENT, 'marginRight', calculatedValue => calculatedValue - width);
    }

    _disableOverFlow() {
      this._saveInitialAttribute(this._element, 'overflow');
      this._element.style.overflow = 'hidden';
    }

    _setElementAttributes(selector, styleProp, callback) {
      const scrollbarWidth = this.getWidth();
      const manipulationCallBack = element => {
        if (element !== this._element && window.innerWidth > element.clientWidth + scrollbarWidth) {
          return
        }

        this._saveInitialAttribute(element, styleProp);
        const calculatedValue = window.getComputedStyle(element)[styleProp];
        element.style[styleProp] = `${callback(Number.parseFloat(calculatedValue))}px`;
      };

      this._applyManipulationCallback(selector, manipulationCallBack);
    }

    reset() {
      this._resetElementAttributes(this._element, 'overflow');
      this._resetElementAttributes(this._element, 'paddingRight');
      this._resetElementAttributes(SELECTOR_FIXED_CONTENT, 'paddingRight');
      this._resetElementAttributes(SELECTOR_STICKY_CONTENT, 'marginRight');
    }

    _saveInitialAttribute(element, styleProp) {
      const actualValue = element.style[styleProp];
      if (actualValue) {
        Manipulator.setDataAttribute(element, styleProp, actualValue);
      }
    }

    _resetElementAttributes(selector, styleProp) {
      const manipulationCallBack = element => {
        const value = Manipulator.getDataAttribute(element, styleProp);
        if (typeof value === 'undefined') {
          element.style.removeProperty(styleProp);
        } else {
          Manipulator.removeDataAttribute(element, styleProp);
          element.style[styleProp] = value;
        }
      };

      this._applyManipulationCallback(selector, manipulationCallBack);
    }

    _applyManipulationCallback(selector, callBack) {
      if (isElement(selector)) {
        callBack(selector);
      } else {
        SelectorEngine.find(selector, this._element).forEach(callBack);
      }
    }

    isOverflowing() {
      return this.getWidth() > 0
    }
  }

  /**
   * --------------------------------------------------------------------------
   * Bootstrap (v5.1.3): dom/data.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  const elementMap = new Map();

  var Data = {
    set(element, key, instance) {
      if (!elementMap.has(element)) {
        elementMap.set(element, new Map());
      }

      const instanceMap = elementMap.get(element);

      // make it clear we only want one instance per element
      // can be removed later when multiple key/instances are fine to be used
      if (!instanceMap.has(key) && instanceMap.size !== 0) {
        // eslint-disable-next-line no-console
        console.error(`Bootstrap doesn't allow more than one instance per element. Bound instance: ${Array.from(instanceMap.keys())[0]}.`);
        return
      }

      instanceMap.set(key, instance);
    },

    get(element, key) {
      if (elementMap.has(element)) {
        return elementMap.get(element).get(key) || null
      }

      return null
    },

    remove(element, key) {
      if (!elementMap.has(element)) {
        return
      }

      const instanceMap = elementMap.get(element);

      instanceMap.delete(key);

      // free up element references if there are no instances left for an element
      if (instanceMap.size === 0) {
        elementMap.delete(element);
      }
    }
  };

  /**
   * --------------------------------------------------------------------------
   * Bootstrap (v5.1.3): base-component.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  const VERSION = '5.1.3';

  class BaseComponent {
    constructor(element) {
      element = getElement(element);

      if (!element) {
        return
      }

      this._element = element;
      Data.set(this._element, this.constructor.DATA_KEY, this);
    }

    dispose() {
      Data.remove(this._element, this.constructor.DATA_KEY);
      EventHandler.off(this._element, this.constructor.EVENT_KEY);

      Object.getOwnPropertyNames(this).forEach(propertyName => {
        this[propertyName] = null;
      });
    }

    _queueCallback(callback, element, isAnimated = true) {
      executeAfterTransition(callback, element, isAnimated);
    }

    /** Static */

    static getInstance(element) {
      return Data.get(getElement(element), this.DATA_KEY)
    }

    static getOrCreateInstance(element, config = {}) {
      return this.getInstance(element) || new this(element, typeof config === 'object' ? config : null)
    }

    static get VERSION() {
      return VERSION
    }

    static get NAME() {
      throw new Error('You have to implement the static method "NAME", for each component!')
    }

    static get DATA_KEY() {
      return `bs.${this.NAME}`
    }

    static get EVENT_KEY() {
      return `.${this.DATA_KEY}`
    }
  }

  /**
   * --------------------------------------------------------------------------
   * Bootstrap (v5.1.3): util/backdrop.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  const Default$2 = {
    className: 'modal-backdrop',
    isVisible: true, // if false, we use the backdrop helper without adding any element to the dom
    isAnimated: false,
    rootElement: 'body', // give the choice to place backdrop under different elements
    clickCallback: null
  };

  const DefaultType$2 = {
    className: 'string',
    isVisible: 'boolean',
    isAnimated: 'boolean',
    rootElement: '(element|string)',
    clickCallback: '(function|null)'
  };
  const NAME$3 = 'backdrop';
  const CLASS_NAME_FADE$2 = 'fade';
  const CLASS_NAME_SHOW$2 = 'show';

  const EVENT_MOUSEDOWN = `mousedown.bs.${NAME$3}`;

  class Backdrop {
    constructor(config) {
      this._config = this._getConfig(config);
      this._isAppended = false;
      this._element = null;
    }

    show(callback) {
      if (!this._config.isVisible) {
        execute(callback);
        return
      }

      this._append();

      if (this._config.isAnimated) {
        reflow(this._getElement());
      }

      this._getElement().classList.add(CLASS_NAME_SHOW$2);

      this._emulateAnimation(() => {
        execute(callback);
      });
    }

    hide(callback) {
      if (!this._config.isVisible) {
        execute(callback);
        return
      }

      this._getElement().classList.remove(CLASS_NAME_SHOW$2);

      this._emulateAnimation(() => {
        this.dispose();
        execute(callback);
      });
    }

    // Private

    _getElement() {
      if (!this._element) {
        const backdrop = document.createElement('div');
        backdrop.className = this._config.className;
        if (this._config.isAnimated) {
          backdrop.classList.add(CLASS_NAME_FADE$2);
        }

        this._element = backdrop;
      }

      return this._element
    }

    _getConfig(config) {
      config = {
        ...Default$2,
        ...(typeof config === 'object' ? config : {})
      };

      // use getElement() with the default "body" to get a fresh Element on each instantiation
      config.rootElement = getElement(config.rootElement);
      typeCheckConfig(NAME$3, config, DefaultType$2);
      return config
    }

    _append() {
      if (this._isAppended) {
        return
      }

      this._config.rootElement.append(this._getElement());

      EventHandler.on(this._getElement(), EVENT_MOUSEDOWN, () => {
        execute(this._config.clickCallback);
      });

      this._isAppended = true;
    }

    dispose() {
      if (!this._isAppended) {
        return
      }

      EventHandler.off(this._element, EVENT_MOUSEDOWN);

      this._element.remove();
      this._isAppended = false;
    }

    _emulateAnimation(callback) {
      executeAfterTransition(callback, this._getElement(), this._config.isAnimated);
    }
  }

  /**
   * --------------------------------------------------------------------------
   * Bootstrap (v5.1.3): util/focustrap.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  const Default$1 = {
    trapElement: null, // The element to trap focus inside of
    autofocus: true
  };

  const DefaultType$1 = {
    trapElement: 'element',
    autofocus: 'boolean'
  };

  const NAME$2 = 'focustrap';
  const DATA_KEY$2 = 'bs.focustrap';
  const EVENT_KEY$2 = `.${DATA_KEY$2}`;
  const EVENT_FOCUSIN = `focusin${EVENT_KEY$2}`;
  const EVENT_KEYDOWN_TAB = `keydown.tab${EVENT_KEY$2}`;

  const TAB_KEY = 'Tab';
  const TAB_NAV_FORWARD = 'forward';
  const TAB_NAV_BACKWARD = 'backward';

  class FocusTrap {
    constructor(config) {
      this._config = this._getConfig(config);
      this._isActive = false;
      this._lastTabNavDirection = null;
    }

    activate() {
      const { trapElement, autofocus } = this._config;

      if (this._isActive) {
        return
      }

      if (autofocus) {
        trapElement.focus();
      }

      EventHandler.off(document, EVENT_KEY$2); // guard against infinite focus loop
      EventHandler.on(document, EVENT_FOCUSIN, event => this._handleFocusin(event));
      EventHandler.on(document, EVENT_KEYDOWN_TAB, event => this._handleKeydown(event));

      this._isActive = true;
    }

    deactivate() {
      if (!this._isActive) {
        return
      }

      this._isActive = false;
      EventHandler.off(document, EVENT_KEY$2);
    }

    // Private

    _handleFocusin(event) {
      const { target } = event;
      const { trapElement } = this._config;

      if (target === document || target === trapElement || trapElement.contains(target)) {
        return
      }

      const elements = SelectorEngine.focusableChildren(trapElement);

      if (elements.length === 0) {
        trapElement.focus();
      } else if (this._lastTabNavDirection === TAB_NAV_BACKWARD) {
        elements[elements.length - 1].focus();
      } else {
        elements[0].focus();
      }
    }

    _handleKeydown(event) {
      if (event.key !== TAB_KEY) {
        return
      }

      this._lastTabNavDirection = event.shiftKey ? TAB_NAV_BACKWARD : TAB_NAV_FORWARD;
    }

    _getConfig(config) {
      config = {
        ...Default$1,
        ...(typeof config === 'object' ? config : {})
      };
      typeCheckConfig(NAME$2, config, DefaultType$1);
      return config
    }
  }

  /**
   * --------------------------------------------------------------------------
   * Bootstrap (v5.1.3): util/component-functions.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  const enableDismissTrigger = (component, method = 'hide') => {
    const clickEvent = `click.dismiss${component.EVENT_KEY}`;
    const name = component.NAME;

    EventHandler.on(document, clickEvent, `[data-bs-dismiss="${name}"]`, function (event) {
      if (['A', 'AREA'].includes(this.tagName)) {
        event.preventDefault();
      }

      if (isDisabled(this)) {
        return
      }

      const target = getElementFromSelector(this) || this.closest(`.${name}`);
      const instance = component.getOrCreateInstance(target);

      // Method argument is left, for Alert and only, as it doesn't implement the 'hide' method
      instance[method]();
    });
  };

  /**
   * --------------------------------------------------------------------------
   * Bootstrap (v5.1.3): modal.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  const NAME$1 = 'modal';
  const DATA_KEY$1 = 'bs.modal';
  const EVENT_KEY$1 = `.${DATA_KEY$1}`;
  const DATA_API_KEY$1 = '.data-api';
  const ESCAPE_KEY = 'Escape';

  const Default = {
    backdrop: true,
    keyboard: true,
    focus: true
  };

  const DefaultType = {
    backdrop: '(boolean|string)',
    keyboard: 'boolean',
    focus: 'boolean'
  };

  const EVENT_HIDE$1 = `hide${EVENT_KEY$1}`;
  const EVENT_HIDE_PREVENTED = `hidePrevented${EVENT_KEY$1}`;
  const EVENT_HIDDEN$1 = `hidden${EVENT_KEY$1}`;
  const EVENT_SHOW$1 = `show${EVENT_KEY$1}`;
  const EVENT_SHOWN$1 = `shown${EVENT_KEY$1}`;
  const EVENT_RESIZE = `resize${EVENT_KEY$1}`;
  const EVENT_CLICK_DISMISS = `click.dismiss${EVENT_KEY$1}`;
  const EVENT_KEYDOWN_DISMISS = `keydown.dismiss${EVENT_KEY$1}`;
  const EVENT_MOUSEUP_DISMISS = `mouseup.dismiss${EVENT_KEY$1}`;
  const EVENT_MOUSEDOWN_DISMISS = `mousedown.dismiss${EVENT_KEY$1}`;
  const EVENT_CLICK_DATA_API$1 = `click${EVENT_KEY$1}${DATA_API_KEY$1}`;

  const CLASS_NAME_OPEN = 'modal-open';
  const CLASS_NAME_FADE$1 = 'fade';
  const CLASS_NAME_SHOW$1 = 'show';
  const CLASS_NAME_STATIC = 'modal-static';

  const OPEN_SELECTOR = '.modal.show';
  const SELECTOR_DIALOG = '.modal-dialog';
  const SELECTOR_MODAL_BODY = '.modal-body';
  const SELECTOR_DATA_TOGGLE$1 = '[data-bs-toggle="modal"]';

  /**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

  class Modal extends BaseComponent {
    constructor(element, config) {
      super(element);

      this._config = this._getConfig(config);
      this._dialog = SelectorEngine.findOne(SELECTOR_DIALOG, this._element);
      this._backdrop = this._initializeBackDrop();
      this._focustrap = this._initializeFocusTrap();
      this._isShown = false;
      this._ignoreBackdropClick = false;
      this._isTransitioning = false;
      this._scrollBar = new ScrollBarHelper();
    }

    // Getters

    static get Default() {
      return Default
    }

    static get NAME() {
      return NAME$1
    }

    // Public

    toggle(relatedTarget) {
      return this._isShown ? this.hide() : this.show(relatedTarget)
    }

    show(relatedTarget) {
      if (this._isShown || this._isTransitioning) {
        return
      }

      const showEvent = EventHandler.trigger(this._element, EVENT_SHOW$1, {
        relatedTarget
      });

      if (showEvent.defaultPrevented) {
        return
      }

      this._isShown = true;

      if (this._isAnimated()) {
        this._isTransitioning = true;
      }

      this._scrollBar.hide();

      document.body.classList.add(CLASS_NAME_OPEN);

      this._adjustDialog();

      this._setEscapeEvent();
      this._setResizeEvent();

      EventHandler.on(this._dialog, EVENT_MOUSEDOWN_DISMISS, () => {
        EventHandler.one(this._element, EVENT_MOUSEUP_DISMISS, event => {
          if (event.target === this._element) {
            this._ignoreBackdropClick = true;
          }
        });
      });

      this._showBackdrop(() => this._showElement(relatedTarget));
    }

    hide() {
      if (!this._isShown || this._isTransitioning) {
        return
      }

      const hideEvent = EventHandler.trigger(this._element, EVENT_HIDE$1);

      if (hideEvent.defaultPrevented) {
        return
      }

      this._isShown = false;
      const isAnimated = this._isAnimated();

      if (isAnimated) {
        this._isTransitioning = true;
      }

      this._setEscapeEvent();
      this._setResizeEvent();

      this._focustrap.deactivate();

      this._element.classList.remove(CLASS_NAME_SHOW$1);

      EventHandler.off(this._element, EVENT_CLICK_DISMISS);
      EventHandler.off(this._dialog, EVENT_MOUSEDOWN_DISMISS);

      this._queueCallback(() => this._hideModal(), this._element, isAnimated);
    }

    dispose() {
      [window, this._dialog]
        .forEach(htmlElement => EventHandler.off(htmlElement, EVENT_KEY$1));

      this._backdrop.dispose();
      this._focustrap.deactivate();
      super.dispose();
    }

    handleUpdate() {
      this._adjustDialog();
    }

    // Private

    _initializeBackDrop() {
      return new Backdrop({
        isVisible: Boolean(this._config.backdrop), // 'static' option will be translated to true, and booleans will keep their value
        isAnimated: this._isAnimated()
      })
    }

    _initializeFocusTrap() {
      return new FocusTrap({
        trapElement: this._element
      })
    }

    _getConfig(config) {
      config = {
        ...Default,
        ...Manipulator.getDataAttributes(this._element),
        ...(typeof config === 'object' ? config : {})
      };
      typeCheckConfig(NAME$1, config, DefaultType);
      return config
    }

    _showElement(relatedTarget) {
      const isAnimated = this._isAnimated();
      const modalBody = SelectorEngine.findOne(SELECTOR_MODAL_BODY, this._dialog);

      if (!this._element.parentNode || this._element.parentNode.nodeType !== Node.ELEMENT_NODE) {
        // Don't move modal's DOM position
        document.body.append(this._element);
      }

      this._element.style.display = 'block';
      this._element.removeAttribute('aria-hidden');
      this._element.setAttribute('aria-modal', true);
      this._element.setAttribute('role', 'dialog');
      this._element.scrollTop = 0;

      if (modalBody) {
        modalBody.scrollTop = 0;
      }

      if (isAnimated) {
        reflow(this._element);
      }

      this._element.classList.add(CLASS_NAME_SHOW$1);

      const transitionComplete = () => {
        if (this._config.focus) {
          this._focustrap.activate();
        }

        this._isTransitioning = false;
        EventHandler.trigger(this._element, EVENT_SHOWN$1, {
          relatedTarget
        });
        this._resetAndDisplayIframe(); 
      };
      this._queueCallback(transitionComplete, this._dialog, isAnimated);
    }

    _resetAndDisplayIframe() {
      const iframe = this._element.querySelector('iframe');
    
      if (iframe && iframe.dataset.originalSrc) {
        iframe.src = iframe.dataset.originalSrc;
      }
    }
    _setEscapeEvent() {
      if (this._isShown) {
        EventHandler.on(this._element, EVENT_KEYDOWN_DISMISS, event => {
          if (this._config.keyboard && event.key === ESCAPE_KEY) {
            event.preventDefault();
            this.hide();
          } else if (!this._config.keyboard && event.key === ESCAPE_KEY) {
            this._triggerBackdropTransition();
          }
        });
      } else {
        EventHandler.off(this._element, EVENT_KEYDOWN_DISMISS);
      }
    }

    _setResizeEvent() {
      if (this._isShown) {
        EventHandler.on(window, EVENT_RESIZE, () => this._adjustDialog());
      } else {
        EventHandler.off(window, EVENT_RESIZE);
      }
    }

    _hideModal() {
      this._element.style.display = 'none';
      this._element.setAttribute('aria-hidden', true);
      this._element.removeAttribute('aria-modal');
      this._element.removeAttribute('role');
      this._isTransitioning = false;
      this._backdrop.hide(() => {
        document.body.classList.remove(CLASS_NAME_OPEN);
        this._resetAdjustments();
        this._scrollBar.reset();
        EventHandler.trigger(this._element, EVENT_HIDDEN$1);
      });
      this._hideVideoOnModalClose(); 
    }

    _hideVideoOnModalClose() {
      const iframe = this._element.querySelector('iframe');
      
      if(iframe) {
        iframe.dataset.originalSrc = iframe.src;
        iframe.removeAttribute('src');
      }
    }

    _showBackdrop(callback) {
      EventHandler.on(this._element, EVENT_CLICK_DISMISS, event => {
        if (this._ignoreBackdropClick) {
          this._ignoreBackdropClick = false;
          return
        }

        if (event.target !== event.currentTarget) {
          return
        }

        if (this._config.backdrop === true) {
          this.hide();
        } else if (this._config.backdrop === 'static') {
          this._triggerBackdropTransition();
        }
      });

      this._backdrop.show(callback);
    }

    _isAnimated() {
      return this._element.classList.contains(CLASS_NAME_FADE$1)
    }

    _triggerBackdropTransition() {
      const hideEvent = EventHandler.trigger(this._element, EVENT_HIDE_PREVENTED);
      if (hideEvent.defaultPrevented) {
        return
      }

      const { classList, scrollHeight, style } = this._element;
      const isModalOverflowing = scrollHeight > document.documentElement.clientHeight;

      // return if the following background transition hasn't yet completed
      if ((!isModalOverflowing && style.overflowY === 'hidden') || classList.contains(CLASS_NAME_STATIC)) {
        return
      }

      if (!isModalOverflowing) {
        style.overflowY = 'hidden';
      }

      classList.add(CLASS_NAME_STATIC);
      this._queueCallback(() => {
        classList.remove(CLASS_NAME_STATIC);
        if (!isModalOverflowing) {
          this._queueCallback(() => {
            style.overflowY = '';
          }, this._dialog);
        }
      }, this._dialog);

      this._element.focus();
    }

    // ----------------------------------------------------------------------
    // the following methods are used to handle overflowing modals
    // ----------------------------------------------------------------------

    _adjustDialog() {
      const isModalOverflowing = this._element.scrollHeight > document.documentElement.clientHeight;
      const scrollbarWidth = this._scrollBar.getWidth();
      const isBodyOverflowing = scrollbarWidth > 0;

      if ((!isBodyOverflowing && isModalOverflowing && !isRTL()) || (isBodyOverflowing && !isModalOverflowing && isRTL())) {
        this._element.style.paddingLeft = `${scrollbarWidth}px`;
      }

      if ((isBodyOverflowing && !isModalOverflowing && !isRTL()) || (!isBodyOverflowing && isModalOverflowing && isRTL())) {
        this._element.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    _resetAdjustments() {
      this._element.style.paddingLeft = '';
      this._element.style.paddingRight = '';
    }

    // Static

    static jQueryInterface(config, relatedTarget) {
      return this.each(function () {
        const data = Modal.getOrCreateInstance(this, config);

        if (typeof config !== 'string') {
          return
        }

        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config](relatedTarget);
      })
    }
  }

  /**
   * ------------------------------------------------------------------------
   * Data Api implementation
   * ------------------------------------------------------------------------
   */

  EventHandler.on(document, EVENT_CLICK_DATA_API$1, SELECTOR_DATA_TOGGLE$1, function (event) {
    const target = getElementFromSelector(this);

    if (['A', 'AREA'].includes(this.tagName)) {
      event.preventDefault();
    }

    EventHandler.one(target, EVENT_SHOW$1, showEvent => {
      if (showEvent.defaultPrevented) {
        // only register focus restorer if modal will actually get shown
        return
      }

      EventHandler.one(target, EVENT_HIDDEN$1, () => {
        if (isVisible(this)) {
          this.focus();
        }
      });
    });

    // avoid conflict when clicking modal toggler while another one is open
    const allReadyOpen = SelectorEngine.findOne(OPEN_SELECTOR);
    if (allReadyOpen) {
      Modal.getInstance(allReadyOpen).hide();
    }

    const data = Modal.getOrCreateInstance(target);

    data.toggle(this);
  });

  enableDismissTrigger(Modal);

  /**
   * ------------------------------------------------------------------------
   * jQuery
   * ------------------------------------------------------------------------
   * add .Modal to jQuery only if jQuery is present
   */

  defineJQueryPlugin(Modal);

  /**
   * --------------------------------------------------------------------------
   * Bootstrap (v5.1.3): tab.js
   * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
   * --------------------------------------------------------------------------
   */

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  const NAME = 'tab';
  const DATA_KEY = 'bs.tab';
  const EVENT_KEY = `.${DATA_KEY}`;
  const DATA_API_KEY = '.data-api';

  const EVENT_HIDE = `hide${EVENT_KEY}`;
  const EVENT_HIDDEN = `hidden${EVENT_KEY}`;
  const EVENT_SHOW = `show${EVENT_KEY}`;
  const EVENT_SHOWN = `shown${EVENT_KEY}`;
  const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;

  const CLASS_NAME_DROPDOWN_MENU = 'dropdown-menu';
  const CLASS_NAME_ACTIVE = 'active';
  const CLASS_NAME_FADE = 'fade';
  const CLASS_NAME_SHOW = 'show';

  const SELECTOR_DROPDOWN = '.dropdown';
  const SELECTOR_NAV_LIST_GROUP = '.nav, .list-group';
  const SELECTOR_ACTIVE = '.active';
  const SELECTOR_ACTIVE_UL = ':scope > li > .active';
  const SELECTOR_DATA_TOGGLE = '[data-bs-toggle="tab"], [data-bs-toggle="pill"], [data-bs-toggle="list"]';
  const SELECTOR_DROPDOWN_TOGGLE = '.dropdown-toggle';
  const SELECTOR_DROPDOWN_ACTIVE_CHILD = ':scope > .dropdown-menu .active';

  /**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

  class Tab extends BaseComponent {
    // Getters

    static get NAME() {
      return NAME
    }

    // Public

    show() {
      if ((this._element.parentNode &&
        this._element.parentNode.nodeType === Node.ELEMENT_NODE &&
        this._element.classList.contains(CLASS_NAME_ACTIVE))) {
        return
      }

      let previous;
      const target = getElementFromSelector(this._element);
      const listElement = this._element.closest(SELECTOR_NAV_LIST_GROUP);

      if (listElement) {
        const itemSelector = listElement.nodeName === 'UL' || listElement.nodeName === 'OL' ? SELECTOR_ACTIVE_UL : SELECTOR_ACTIVE;
        previous = SelectorEngine.find(itemSelector, listElement);
        previous = previous[previous.length - 1];
      }

      const hideEvent = previous ?
        EventHandler.trigger(previous, EVENT_HIDE, {
          relatedTarget: this._element
        }) :
        null;

      const showEvent = EventHandler.trigger(this._element, EVENT_SHOW, {
        relatedTarget: previous
      });

      if (showEvent.defaultPrevented || (hideEvent !== null && hideEvent.defaultPrevented)) {
        return
      }

      this._activate(this._element, listElement);

      const complete = () => {
        EventHandler.trigger(previous, EVENT_HIDDEN, {
          relatedTarget: this._element
        });
        EventHandler.trigger(this._element, EVENT_SHOWN, {
          relatedTarget: previous
        });
      };

      if (target) {
        this._activate(target, target.parentNode, complete);
      } else {
        complete();
      }
    }

    // Private

    _activate(element, container, callback) {
      const activeElements = container && (container.nodeName === 'UL' || container.nodeName === 'OL') ?
        SelectorEngine.find(SELECTOR_ACTIVE_UL, container) :
        SelectorEngine.children(container, SELECTOR_ACTIVE);

      const active = activeElements[0];
      const isTransitioning = callback && (active && active.classList.contains(CLASS_NAME_FADE));

      const complete = () => this._transitionComplete(element, active, callback);

      if (active && isTransitioning) {
        active.classList.remove(CLASS_NAME_SHOW);
        this._queueCallback(complete, element, true);
      } else {
        complete();
      }
    }

    _transitionComplete(element, active, callback) {
      if (active) {
        active.classList.remove(CLASS_NAME_ACTIVE);

        const dropdownChild = SelectorEngine.findOne(SELECTOR_DROPDOWN_ACTIVE_CHILD, active.parentNode);

        if (dropdownChild) {
          dropdownChild.classList.remove(CLASS_NAME_ACTIVE);
        }

        if (active.getAttribute('role') === 'tab') {
          active.setAttribute('aria-selected', false);
        }
      }

      element.classList.add(CLASS_NAME_ACTIVE);
      if (element.getAttribute('role') === 'tab') {
        element.setAttribute('aria-selected', true);
      }

      reflow(element);

      if (element.classList.contains(CLASS_NAME_FADE)) {
        element.classList.add(CLASS_NAME_SHOW);
      }

      let parent = element.parentNode;
      if (parent && parent.nodeName === 'LI') {
        parent = parent.parentNode;
      }

      if (parent && parent.classList.contains(CLASS_NAME_DROPDOWN_MENU)) {
        const dropdownElement = element.closest(SELECTOR_DROPDOWN);

        if (dropdownElement) {
          SelectorEngine.find(SELECTOR_DROPDOWN_TOGGLE, dropdownElement)
            .forEach(dropdown => dropdown.classList.add(CLASS_NAME_ACTIVE));
        }

        element.setAttribute('aria-expanded', true);
      }

      if (callback) {
        callback();
      }
    }

    // Static

    static jQueryInterface(config) {
      return this.each(function () {
        const data = Tab.getOrCreateInstance(this);

        if (typeof config === 'string') {
          if (typeof data[config] === 'undefined') {
            throw new TypeError(`No method named "${config}"`)
          }

          data[config]();
        }
      })
    }
  }

  /**
   * ------------------------------------------------------------------------
   * Data Api implementation
   * ------------------------------------------------------------------------
   */

  EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
    if (['A', 'AREA'].includes(this.tagName)) {
      event.preventDefault();
    }

    if (isDisabled(this)) {
      return
    }

    const data = Tab.getOrCreateInstance(this);
    data.show();
  });

  /**
   * ------------------------------------------------------------------------
   * jQuery
   * ------------------------------------------------------------------------
   * add .Tab to jQuery only if jQuery is present
   */

  defineJQueryPlugin(Tab);

  class Accordion {
      static main() {
          const bodyClass = document.querySelector('body').classList;
          const isGov = bodyClass.contains('gov') ? true : false;

          const accordions = document.querySelectorAll('.accordion-trigger');
          if (accordions) {
              init();
          }

          function init() {
              accordions.forEach((accordion) => {
                  accordion.addEventListener('click', toggleAccordion);               
              });            
          }

          function toggleAccordion(e) {
              e.preventDefault();
              const accordionItem = e.target.closest('.accordion');            
              const toggle = e.currentTarget;            

              if (!accordionItem.classList.contains('open')) {
                  accordionItem.classList.add('open');
                  toggle.setAttribute('aria-expanded', true);

                  // Gov uses + and - icons, not chevrons like Microsites
                  if (isGov) {
                      const toggleIcon = e.currentTarget.querySelector('.accordion-icon svg use');
                      if (toggleIcon) {
                          let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#accordion-minus');
                          toggleIcon.setAttribute('href', iconHref);
                      }
                  }
              }
              else {
                  accordionItem.classList.remove('open');
                  toggle.setAttribute('aria-expanded', false);                
                  toggle.blur();

                  // Gov uses + and - circles, not chevrons like Microsites
                  if (isGov) {
                      const toggleIcon = e.currentTarget.querySelector('.accordion-icon svg use');
                      if (toggleIcon) {
                          let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#accordion-plus');
                          toggleIcon.setAttribute('href', iconHref);
                      }
                  }
              }
          }

          // function updateAccordionScrollbar() {
          //     accordions.forEach((accordion) => {
          //         const content = accordion.querySelector('.accordion-content');
          //         if (content.scrollHeight > accordion.clientHeight) {
          //             accordion.style.overflowY = 'scroll';
          //         } else {
          //             accordion.style.overflowY = 'hidden';
          //         }
          //     });
          // }
      }
  }

  class Alerts {
      static main() {

          init();

          function init() {            
              const alertCloseBtns = document.querySelectorAll('.alert .js-close-alert');
              
              if (alertCloseBtns) {
                  shouldRender();

                  alertCloseBtns.forEach((closeBtn) => {
                      closeBtn.addEventListener('click', closeAlert);
                  });
              }
          }

          // check sessionStorage if alert has been closed or not already
          function shouldRender() {{
              const alerts = document.querySelectorAll('.alert');

              alerts.forEach((alert) => {
                  const alertId = alert.getAttribute('id');
                  const shouldRender = sessionStorage.getItem('alert-'+alertId) !== "dismissed";

                  if (shouldRender) {
                      alert.classList.add('show');
                  }
              });
          }}

          function closeAlert(e) {
              e.preventDefault();
              const alertId = e.target.closest('.alert').getAttribute('id');
              
              sessionStorage.setItem('alert-' + alertId, "dismissed");
              document.getElementById(alertId).classList.remove('show');
          }
      }
  }

  /*
      We start by selecting all elements with the class counter and setting up an IntersectionObserver to watch for when they become visible in the viewport.

      When a counter element becomes visible, we call the animateCounter function to start animating the counter from the start value to the end value in increments of the step value.

      The animateCounter function uses a setInterval timer to increment the current value of the counter and update its text content every 5 milliseconds. Once the current value reaches the end value, the timer is cleared and the counter text content is set to the final value.

  */
  class AnimatedStat {
      static main() {
          const counters = document.querySelectorAll('.animated-stat');
          const options = {
              rootMargin: '0px',
              threshold: 0.5
          };

          const observer = new IntersectionObserver(function(entries, observer) {
          entries.forEach(entry => {
              if (entry.isIntersecting) {
                  animateCounter(entry.target);
                  observer.unobserve(entry.target);
              }
          });
          }, options);

          counters.forEach(counter => {
              observer.observe(counter);
          });

          function animateCounter(counter) {
              const counterHTML = counter.querySelector('.counter-num');
              const start = Number(counter.getAttribute('data-start'));
              const end = Number(counter.getAttribute('data-end'));
              const step = Number(counter.getAttribute('data-step'));
              let current = start;

              const timer = setInterval(() => {
                  current += step;
                  counterHTML.innerHTML = current;
                  if (current >= end) {
                      counterHTML.innerHTML = end;
                      clearInterval(timer);
                  }
              }, 5);
          }
      }
  }

  class BlogCards {
      static main() {
          let years = new Set();
          let categories = new Set();
          let categories2 = new Set();
          let categories3 = new Set();

          let activeCategory = "all";
          let activeCategory2 = "all";
          let activeCategory3 = "all";
          let activeYear = "all";
          let activeSort = "all";
          let searchTerm = null;
          let currentPage = 1;
          let index = {
              firstIndex: 0,
              lastIndex: 9
          };
     
          const cardLinks = document.querySelectorAll('.blog-widget .kdx-card a, .event-widget .kdx-card a, .blog-listing .kdx-card a, .event-listing .kdx-card a');
          const initCards = [...document.querySelectorAll('.blog-listing .listing.row > *')];
          
          let filtered = initCards;

          if (cardLinks.length) {
              init();
          }

          function setCategories(){
              initCards.forEach((card) => {
                  const cardEl = card.querySelector(".kdx-card");
                  const categoriesArr = JSON.parse(cardEl.getAttribute("data-categories"));
                  for(const category in categoriesArr){
                      categories.add(categoriesArr[category]);
                  }
              });
              const categorySelect = document.querySelector("#filter-by-category");
              categories.forEach(category => {
                  const categoryOption = document.createElement("option");
                  categoryOption.setAttribute("value", category);
                  categoryOption.innerText = category;
                  categorySelect.appendChild(categoryOption);
              });
          }

          function setCategories2(){
              initCards.forEach((card) => {
                  const cardEl = card.querySelector(".kdx-card");
                  const categoriesArr = JSON.parse(cardEl.getAttribute("data-categories-2"));
                  for(const category in categoriesArr){
                      categories2.add(categoriesArr[category]);
                  }
              });
              const categorySelect = document.querySelector("#filter-by-category-2");
              categories2.forEach(category => {
                  const categoryOption = document.createElement("option");
                  categoryOption.setAttribute("value", category);
                  categoryOption.innerText = category;
                  categorySelect.appendChild(categoryOption);
              });
          }

          function setCategories3(){
              initCards.forEach((card) => {
                  const cardEl = card.querySelector(".kdx-card");
                  const categoriesArr = JSON.parse(cardEl.getAttribute("data-categories-3"));
                  for(const category in categoriesArr){
                      categories3.add(categoriesArr[category]);
                  }
              });
              const categorySelect = document.querySelector("#filter-by-category-3");
              categories3.forEach(category => {
                  const categoryOption = document.createElement("option");
                  categoryOption.setAttribute("value", category);
                  categoryOption.innerText = category;
                  categorySelect.appendChild(categoryOption);
              });
          }

          function setYears(){
              initCards.forEach((card) => {
                  const cardEl = card.querySelector(".kdx-card");
                  const cardContent = cardEl.querySelector(".date")?.textContent;
                  cardContent !== null && cardContent !== undefined && years.add(cardContent.slice(-4));
              });
              const yearsSelect = document.querySelector("#filter-by-year");
              years.forEach((year) => {
                  const yearOption = document.createElement("option");
                  yearOption.setAttribute("value", year);
                  yearOption.innerText = year;
                  yearsSelect.appendChild(yearOption);
              });
          }

          function handlePages(){
              //clear pages
              const list = document.querySelector(".blog-listing .pagination ul");
              if (list==null)
              {
                  return;
              }

              Array.from(list.children).forEach((child) => {
                  child.remove();
              });

              //Add prev and next buttons manually
              list.insertAdjacentHTML("afterbegin", `<li class="previous-page">
            <a href="#" aria-label="Previous page" disabled="">
                <svg><use href="/ResourcePackages/Frontend/public/library/img/icon/sheets/icons.svg#chevron-left"></use></svg>
            </a>
            </li>
            <li class="next-page">
            <a href="#" aria-label="Next page">
                <svg><use href="/ResourcePackages/Frontend/public/library/img/icon/sheets/icons.svg#chevron-right"></use></svg>
            </a>
            </li>`);

              //Then append pages
              const pagination = document.querySelector(".blog-listing .pagination ul li.previous-page");
              const pageAmount = filtered.length ? Math.ceil(filtered.length / 9) : 0;
              if(pageAmount <= 1){
                  document.querySelector(".next-page a").setAttribute("disabled", "true");
                  document.querySelector(".previous-page a").setAttribute("disabled", "true");
              }
              for(var i = pageAmount; i > 0; i--){
                  pagination.insertAdjacentHTML("afterend", `<li><a href="#" data-index=${i} class="${i === 1 ? "active page" : "page"}" aria-label="Jump to page ${i}">${i}</a></li>`);
              }
          }

          function renderPage(){
              filtered.forEach((card) => {
                  card.remove();
              });
         
              const slicedBlogs = filtered.slice(index.firstIndex, index.lastIndex);
              slicedBlogs.forEach((blog) => {
                  document.querySelector(".blog-listing .listing.row").append(blog);
              });
          }

          function sortingAndFiltering(){
              var hasFilters = document.querySelector("#filter-by-category") 
                            || document.querySelector("#filter-by-category-2")
                            || document.querySelector("#filter-by-category-3");
              
              if (!hasFilters)
              {
                  return;
              }

              document.querySelector(".blog-listing .filters form .input-group input[type='submit']").addEventListener("click", (e) => {
                  e.preventDefault();
                  const keyWordSearch = document.querySelector("#keyword-search").value;
                  searchTerm = keyWordSearch ? keyWordSearch : null;
                  filterBlog();
              });

              document.querySelector(".blog-listing .filters .clear-search a").addEventListener("click", (e) => {
                  e.preventDefault();
                  document.querySelector("#keyword-search").value = "";
                  document.querySelector("#sort-by").selectedIndex = "0";
                  document.querySelector("#filter-by-category").selectedIndex = "0";
                  document.querySelector("#filter-by-category-2").selectedIndex = "0";
                  document.querySelector("#filter-by-category-3").selectedIndex = "0";
                  document.querySelector("#filter-by-year").selectedIndex = "0";
                  activeSort = document.querySelector("#sort-by").value;
                  activeCategory = document.querySelector("#filter-by-category").value;
                  activeCategory2 = document.querySelector("#filter-by-category-2").value;
                  activeCategory3 = document.querySelector("#filter-by-category-3").value;
                  activeYear = document.querySelector("#filter-by-year").value;
                  searchTerm = null;
                  filterBlog();
                  sortBlog(filtered);
              });


              if (document.querySelector("#filter-by-category"))
              {
                  document.querySelector("#filter-by-category").addEventListener("change", (e) => {
                      activeCategory = e.target.value;
                      filterBlog();
                  });
              }

              if (document.querySelector("#filter-by-category-2"))
              {
                  document.querySelector("#filter-by-category-2").addEventListener("change", (e) => {
                      activeCategory2 = e.target.value;
                      filterBlog();
                  });
              }

              if (document.querySelector("#filter-by-category-3"))
              {
                  document.querySelector("#filter-by-category-3").addEventListener("change", (e) => {
                      activeCategory3 = e.target.value;
                      filterBlog();
                  });
              }

              if (document.querySelector("#filter-by-year"))
              {
                  document.querySelector("#filter-by-year").addEventListener("change", (e) => {
                      activeYear = e.target.value;
                      filterBlog();
                  });
              }

              if (document.querySelector("#sort-by"))
              {
                  document.querySelector("#sort-by").addEventListener("change", (e) => {
                      activeSort = e.target.value;
                      sortBlog(filtered);
                  });
              }
          }

          function changePage(e, pages, nextButton, prevButton){
              e.preventDefault();
              pages.forEach((page) => page.classList.contains("active") && page.classList.remove("active"));

              currentPage = parseInt(e.target.getAttribute("data-index"));
              index.firstIndex = currentPage === 1 ? 0 : (9 * (currentPage - 1));
              index.lastIndex = currentPage === 1 ? 9 : (9 * (currentPage - 1)) + 9;

              nextButton.removeAttribute("disabled");
              prevButton.removeAttribute("disabled");

              handlePagingStatus(filtered);

              index.firstIndex === 0 ? prevButton.setAttribute("disabled", "true") : prevButton.removeAttribute("disabled");
              filtered[index.lastIndex] === undefined ? nextButton.setAttribute("disabled", "true") : nextButton.removeAttribute("disabled");

              e.target.classList.add("active");
              renderPage();
          }

          function pageControl(){

              const prevButton = document.querySelector(".previous-page a");
              const nextButton = document.querySelector(".next-page a");
              const pages = Array.from(document.querySelectorAll(".blog-listing .pagination ul .page"));

              if (prevButton)
              {
                  prevButton.addEventListener("click", (e) => {
                      currentPage -= 1;
                      index.firstIndex -= 9;
                      index.lastIndex -= 9;

                      pages.forEach((page) => page.classList.contains("active") && page.classList.remove("active"));
                      pages.find((page) => parseInt(page.getAttribute("data-index")) === currentPage).classList.add("active");

                      handlePagingStatus(filtered);

                      nextButton.removeAttribute("disabled");
                      index.firstIndex === 0 ? prevButton.setAttribute("disabled", "true") : prevButton.removeAttribute("disabled");
                      renderPage();
                  });
              }
              if (nextButton)
              {
                  nextButton.addEventListener("click", (e) => {
                      currentPage += 1;
                      index.firstIndex += 9;
                      index.lastIndex += 9;

                      pages.forEach((page) => page.classList.contains("active") && page.classList.remove("active"));
                      pages.find((page) => parseInt(page.getAttribute("data-index")) === currentPage).classList.add("active");

                      handlePagingStatus(filtered);

                      prevButton.removeAttribute("disabled");
                      filtered[index.lastIndex] === undefined ? nextButton.setAttribute("disabled", "true") : prevButton.removeAttribute("disabled");
                      renderPage();
                  });
              }
              if (pages)
              {
                  pages.forEach((page) => {
                      page.addEventListener("click", (e) => {
                          changePage(e, pages, nextButton, prevButton);
                      });
                  });
              }
          }

          function handlePagingStatus(filtered){
              let lastIndex = null;
              for(var i = (index.firstIndex); i < index.lastIndex; i++){
                  if(filtered[i] === undefined){
                      lastIndex = i;
                      break;
                  }
              }

              const pagingStatus = document.querySelector(".paging-status");
              if (pagingStatus)
              {
                  document.querySelector(".paging-status").innerText = `Showing ${index.firstIndex + 1}-${filtered.length < 9 ? filtered.length : (lastIndex !== null ? lastIndex : index.lastIndex)} of ${filtered.length}`;
              }
          }

          function init() {
              //Handle init paging status
              handlePagingStatus(filtered);
             
              //Render first page on load
              renderPage();

              //Handle page buttons
              handlePages();

              //Find Categories and Years.
              setCategories();
              setCategories2();
              setCategories3();
              setYears();


              //Add click listeners to page buttons
              pageControl();
              
              //Add change and click listeners for filtering and sorting
              sortingAndFiltering();


              //Add hover listeners to blog links
              cardLinks.forEach((cardLink) => {
                  const card = cardLink.closest('.kdx-card');
                  
                  cardLink.addEventListener('mouseenter', setHover);
                  cardLink.addEventListener('mouseleave', removeHover);
                  cardLink.addEventListener('focusin', setHover);
                  cardLink.addEventListener('focusout', removeHover);
                                  
                  if (card) {
                      const cardImage = card.querySelector('.card-img-top');

                      if (!cardImage) {
                          card.classList.add('no-image');                               
                      }
                  }
              });
          }

          function sortBlog(filteredCards){
              handlePagingStatus(filteredCards);

              currentPage = 1;
              index.firstIndex = 0;
              index.lastIndex = 9;
              const cardsReplica = filteredCards.sort((a, b) => {
                  //Date Sorting
                  const yearA = a?.querySelector(".date")?.textContent !== undefined && a?.querySelector(".date")?.textContent !== null ? new Date(a.querySelector(".date").textContent) : new Date("1/1/1900");
                  const yearB = b?.querySelector(".date")?.textContent !== undefined && b?.querySelector(".date")?.textContent !== null ? new Date(b.querySelector(".date").textContent) : new Date("1/1/1900");

                  //Author Sorting
                  const authorA = a?.querySelector(".author")?.textContent !== undefined && a?.querySelector(".author")?.textContent !== null ? a.querySelector(".author").textContent.toUpperCase().trim("") : " ";
                  const authorB = b?.querySelector(".author")?.textContent !== undefined && b?.querySelector(".author")?.textContent !== null ? b.querySelector(".author").textContent.toUpperCase().trim("") : " ";

                  //Title Sorting
                  const titleA = a?.querySelector(".card-title a")?.textContent !== undefined && a?.querySelector(".card-title a")?.textContent !== null ? a.querySelector(".card-title a").textContent.toUpperCase().trim("") : " ";
                  const titleB = b?.querySelector(".card-title a")?.textContent !== undefined && b?.querySelector(".card-title a")?.textContent !== null ? b.querySelector(".card-title a").textContent.toUpperCase().trim("") : " ";

                  if(activeSort === "date-descending"){
                      return yearA == yearB ? 0 : (yearA > yearB ? 1 : -1);
                  }else if(activeSort === "date-ascending"){
                      return yearA == yearB ? 0 : (yearA < yearB ? 1 : -1);
                  }else if(activeSort === "author-z-a"){
                      return authorA == authorB ? 0 : (authorA > authorB ? 1 : -1);
                  }else if(activeSort === "author-a-z"){
                      return authorA == authorB ? 0 : (authorA < authorB ? 1 : -1);
                  }else if(activeSort == "title-z-a"){
                      return titleA == titleB ? 0 : (titleA < titleB ? 1 : -1);
                  }else if(activeSort == "title-a-z"){
                      return titleA == titleB ? 0 : (titleA > titleB ? 1 : -1);
                  }
              });
              filtered = [...cardsReplica];
             
              handlePages();
              renderPage();
              pageControl();
          }
          
          function filterBlog(){
                  filtered = [];
                  //Level 1 Filter (categories)
                  const cards = [...initCards];
                  cards.forEach((child) => {
                      child.remove();
                  });
                  let filteredCards = [];
                  if(activeCategory == "all"){
                      filteredCards = [...cards];
                  }else {
                      cards.forEach((card) => {
                          const cardEl = card.querySelector(".kdx-card");
                          const cardCategories = JSON.parse(cardEl.getAttribute("data-categories"));
                          if(cardCategories.includes(activeCategory)){
                              filteredCards.push(card);
                          }
                      });
                  }

                  //Level 2 Filter (categories2)
                  let filteredCards2 = [];
                  if(activeCategory2 == "all"){
                      filteredCards2 = [...filteredCards];
                  }else {
                      filteredCards.forEach((card) => {
                          const cardEl = card.querySelector(".kdx-card");
                          const cardCategories = JSON.parse(cardEl.getAttribute("data-categories-2"));
                          if(cardCategories.includes(activeCategory2)){
                              filteredCards2.push(card);
                          }
                      });
                  }

                  //Level 3 Filter (categories2)
                  let filteredCards3 = [];
                  if(activeCategory3 == "all"){
                      filteredCards3 = [...filteredCards2];
                  }else {
                      filteredCards2.forEach((card) => {
                          const cardEl = card.querySelector(".kdx-card");
                          const cardCategories = JSON.parse(cardEl.getAttribute("data-categories-3"));
                          if(cardCategories.includes(activeCategory3)){
                              filteredCards3.push(card);
                          }
                      });
                  }
        
                  //Level 4 Filter (years)
                  let filteredCards4 = [];
                  if(activeYear == "all"){
                      filteredCards4 = [...filteredCards3];
                  }else {
                      filteredCards3.forEach((card) => {
                          const cardEl = card.querySelector(".kdx-card");
                          if(cardEl?.querySelector(".date")?.textContent.slice(-4) == activeYear){
                              filteredCards4.push(card);
                          }
                      });
                  }

                  //Level 5 Filter (search term)
                  let filteredCards5 = [];
                  if(searchTerm == null){
                      filteredCards4.forEach((card) => {
                          document.querySelector(".blog-listing .listing.row").append(card);
                      });
                      filteredCards5 = filteredCards4;
                  }else {
                      filteredCards4.forEach((card) => {
                          const cardEl = card.querySelector(".kdx-card");
                          if(cardEl?.querySelector(".card-title a").textContent.toUpperCase().includes(searchTerm.toUpperCase())){
                              document.querySelector(".blog-listing .listing.row").append(card);
                              filteredCards5.push(card);
                          }
                      });
                  }

                  index.firstIndex = 0;
                  index.lastIndex = 9;

                  //Sort the filtered list
                  sortBlog(filteredCards5);

                  //Handle pagination
                  
                  handlePages();
                  renderPage();
                  pageControl();


          }

          function setHover(e) {
              e.target.closest('.kdx-card').classList.add('hover');
          }

          function removeHover(e) {
              e.target.closest('.kdx-card').classList.remove('hover');
          }
      }
  }

  class NewsCards {
      static main() {
          let years = new Set();
          let categories = new Set();
          let categories2 = new Set();
          let categories3 = new Set();

          let activeCategory = "all";
          let activeCategory2 = "all";
          let activeCategory3 = "all";
          let activeYear = "all";
          let activeSort = "all";
          let searchTerm = null;
          let currentPage = 1;
          let pageAmount = 0;
          let prevButton = null;
          let nextButton = null;

          let index = {
              firstIndex: 0,
              lastIndex: 9
          };

          let pages = [];
     
          const cardLinks = document.querySelectorAll('.news-widget .kdx-card a, .event-widget .kdx-card a, .news-listing .kdx-card a, .event-listing .kdx-card a');
          const initCards = [...document.querySelectorAll('.news-listing .listing.row > *')];
          
          let filtered = initCards;

          if (cardLinks.length) {
              init();
          }

          function setCategories(){
              initCards.forEach((card) => {
                  const cardEl = card.querySelector(".kdx-card");
                  const categoriesArr = JSON.parse(cardEl.getAttribute("data-categories"));
                  for(const category in categoriesArr){
                      categories.add(categoriesArr[category]);
                  }
              });
              const categorySelect = document.querySelector("#filter-by-category");
              categories.forEach(category => {
                  const categoryOption = document.createElement("option");
                  categoryOption.setAttribute("value", category);
                  categoryOption.innerText = category;
                  categorySelect.appendChild(categoryOption);
              });
          }

          function setCategories2(){
              initCards.forEach((card) => {
                  const cardEl = card.querySelector(".kdx-card");
                  const categoriesArr = JSON.parse(cardEl.getAttribute("data-categories-2"));
                  for(const category in categoriesArr){
                      categories2.add(categoriesArr[category]);
                  }
              });
              const categorySelect = document.querySelector("#filter-by-category-2");
              categories2.forEach(category => {
                  const categoryOption = document.createElement("option");
                  categoryOption.setAttribute("value", category);
                  categoryOption.innerText = category;
                  categorySelect.appendChild(categoryOption);
              });
          }

          function setCategories3(){
              initCards.forEach((card) => {
                  const cardEl = card.querySelector(".kdx-card");
                  const categoriesArr = JSON.parse(cardEl.getAttribute("data-categories-3"));
                  for(const category in categoriesArr){
                      categories3.add(categoriesArr[category]);
                  }
              });
              const categorySelect = document.querySelector("#filter-by-category-3");
              categories3.forEach(category => {
                  const categoryOption = document.createElement("option");
                  categoryOption.setAttribute("value", category);
                  categoryOption.innerText = category;
                  categorySelect.appendChild(categoryOption);
              });
          }

          function setYears(){
              initCards.forEach((card) => {
                  const cardEl = card.querySelector(".kdx-card");
                  const cardContent = cardEl.querySelector(".date")?.textContent;
                  cardContent !== null && cardContent !== undefined && years.add(cardContent.slice(-4));
              });
              const yearsSelect = document.querySelector("#filter-by-year");
              years.forEach((year) => {
                  const yearOption = document.createElement("option");
                  yearOption.setAttribute("value", year);
                  yearOption.innerText = year;
                  yearsSelect.appendChild(yearOption);
              });
          }

          function handlePages(){
              //clear pages
              const list = document.querySelector(".news-listing .pagination ul");
              if (list==null)
              {
                  return;
              }

              Array.from(list.children).forEach((child) => {
                  child.remove();
              });

              list.innerHTML = "";

              //Add prev and next buttons manually
              list.insertAdjacentHTML("afterbegin", `<li class="previous-page">
            <a href="#" aria-label="Previous page" disabled="">
                <svg><use href="/ResourcePackages/Frontend/public/library/img/icon/sheets/icons.svg#chevron-left"></use></svg>
            </a>
            </li>
            <li class="next-page">
            <a href="#" aria-label="Next page">
                <svg><use href="/ResourcePackages/Frontend/public/library/img/icon/sheets/icons.svg#chevron-right"></use></svg>
            </a>
            </li>`);

              //Then append pages
              const pagination = document.querySelector(".news-listing .pagination ul li.previous-page");
              pageAmount = filtered.length ? Math.ceil(filtered.length / 9) : 0;

              if(pageAmount <= 1){
                  document.querySelector(".next-page a").setAttribute("disabled", "true");
                  document.querySelector(".previous-page a").setAttribute("disabled", "true");
              }

              if (!currentPage)
              {
                  currentPage = 1;
              }

              if (pageAmount <= 11)
              {
                  for(var i = pageAmount; i > 0; i--){
                      pagination.insertAdjacentHTML("afterend", `<li><a href="#" data-index=${i} class="${i === currentPage ? "active page" : "page"}" aria-label="Jump to page ${i}">${i}</a></li>`);
                  }
              }
              //handle paging for large number of pages
              else
              {
                  for(var i = pageAmount; i > pageAmount-3; i--){
                      pagination.insertAdjacentHTML("afterend", `<li><a href="#" data-index=${i} class="${i === currentPage ? "active page" : "page"}" aria-label="Jump to page ${i}">${i}</a></li>`);
                  }
                  pagination.insertAdjacentHTML("afterend", "...&nbsp;");
                  if (currentPage > 3 && currentPage <= pageAmount-3) {
                      for(var i = currentPage+3; i > currentPage-3; i--){
                          if (i > 3 && i <= pageAmount - 3)
                          {
                              pagination.insertAdjacentHTML("afterend", `<li><a href="#" data-index=${i} class="${i === currentPage ? "active page" : "page"}" aria-label="Jump to page ${i}">${i}</a></li>`);
                          }
                      }
                      pagination.insertAdjacentHTML("afterend", "...&nbsp;");
                  }
                  for(var i = 3; i > 0; i--){
                      pagination.insertAdjacentHTML("afterend", `<li><a href="#" data-index=${i} class="${i === currentPage ? "active page" : "page"}" aria-label="Jump to page ${i}">${i}</a></li>`);
                  }
              }
          }

          function renderPage(){
              filtered.forEach((card) => {
                  card.remove();
              });
         
              const slicedNews = filtered.slice(index.firstIndex, index.lastIndex);
              slicedNews.forEach((news) => {
                  document.querySelector(".news-listing .listing.row").append(news);
              });
          }

          function sortingAndFiltering(){
              var hasFilters = document.querySelector("#filter-by-category") 
                            || document.querySelector("#filter-by-category-2")
                            || document.querySelector("#filter-by-category-3");
              
              if (!hasFilters)
              {
                  return;
              }

              document.querySelector(".news-listing .filters form .input-group input[type='submit']").addEventListener("click", (e) => {
                  e.preventDefault();
                  const keyWordSearch = document.querySelector("#keyword-search").value;
                  searchTerm = keyWordSearch ? keyWordSearch : null;
                  filterNews();
              });

              document.querySelector(".news-listing .filters .clear-search a").addEventListener("click", (e) => {
                  e.preventDefault();
                  localStorage.setItem("newsPage", 1);
                  document.querySelector("#keyword-search").value = "";
                  document.querySelector("#sort-by").selectedIndex = "0";
                  document.querySelector("#filter-by-category").selectedIndex = "0";
                  document.querySelector("#filter-by-category-2").selectedIndex = "0";
                  document.querySelector("#filter-by-category-3").selectedIndex = "0";
                  document.querySelector("#filter-by-year").selectedIndex = "0";
                  activeSort = document.querySelector("#sort-by")?.value;
                  activeCategory = document.querySelector("#filter-by-category")?.value;
                  activeCategory2 = document.querySelector("#filter-by-category-2")?.value;
                  activeCategory3 = document.querySelector("#filter-by-category-3")?.value;
                  activeYear = document.querySelector("#filter-by-year")?.value;
                  searchTerm = null;
                  filterNews();
                  sortNews(filtered);
              });


              if (document.querySelector("#filter-by-category"))
              {
                  document.querySelector("#filter-by-category").addEventListener("change", (e) => {
                      activeCategory = e.target.value;
                      filterNews();
                  });
              }

              if (document.querySelector("#filter-by-category-2"))
              {
                  document.querySelector("#filter-by-category-2").addEventListener("change", (e) => {
                      activeCategory2 = e.target.value;
                      filterNews();
                  });
              }

              if (document.querySelector("#filter-by-category-3"))
              {
                  document.querySelector("#filter-by-category-3").addEventListener("change", (e) => {
                      activeCategory3 = e.target.value;
                      filterNews();
                  });
              }

              if (document.querySelector("#filter-by-year"))
              {
                  document.querySelector("#filter-by-year").addEventListener("change", (e) => {
                      activeYear = e.target.value;
                      filterNews();
                  });
              }

              if (document.querySelector("#sort-by"))
              {
                  document.querySelector("#sort-by").addEventListener("change", (e) => {
                      activeSort = e.target.value;
                      sortNews(filtered);
                  });
              }
          }

          function changePage(e, pages, nextButton, prevButton){
              e.preventDefault();

              pages.forEach((page) => page.classList.contains("active") && page.classList.remove("active"));

              currentPage = parseInt(e.target.getAttribute("data-index"));

              handlePages();
              pageControl();
              index.firstIndex = currentPage === 1 ? 0 : (9 * (currentPage - 1));
              index.lastIndex = currentPage === 1 ? 9 : (9 * (currentPage - 1)) + 9;

              handlePagingStatus(filtered);
               
              //if user clicked on page 1, disable prev button, otherwise remove disabled attribute
              currentPage === 1 ? document.querySelector(".previous-page a").setAttribute("disabled", "true") : 
                                      document.querySelector(".previous-page a").removeAttribute("disabled");

              //if user clicked on last page, disable next button, otherwise remove disabled attribute
              currentPage === pageAmount ? document.querySelector(".next-page a").setAttribute("disabled", "true") : 
                                      document.querySelector(".next-page a").removeAttribute("disabled");

              e.target.classList.add("active");
              localStorage.setItem("newsPage", currentPage);
              renderPage();
              
          }

          function pageControl(){

              prevButton = document.querySelector(".previous-page a");
              nextButton = document.querySelector(".next-page a");
              pages = Array.from(document.querySelectorAll(".news-listing .pagination ul .page"));

              if (prevButton)
              {
                  prevButton.addEventListener("click", (e) => {
                      currentPage -= 1;
                      index.firstIndex -= 9;
                      index.lastIndex -= 9;

                      handlePages();
                      pageControl();

                      pages.forEach((page) => page.classList.contains("active") && page.classList.remove("active"));
                      pages.find((page) => parseInt(page.getAttribute("data-index")) === currentPage).classList.add("active");

                      handlePagingStatus(filtered);

                      nextButton.removeAttribute("disabled");

                      currentPage === 1 ? prevButton.setAttribute("disabled", "true") : prevButton.removeAttribute("disabled");
                      renderPage();
                      
                  });
              }
              if (nextButton)
              {
                  nextButton.addEventListener("click", (e) => {
                      currentPage += 1;
                      index.firstIndex += 9;
                      index.lastIndex += 9;

                      handlePages();
                      pageControl();

                      pages.forEach((page) => page.classList.contains("active") && page.classList.remove("active"));
                      pages.find((page) => parseInt(page.getAttribute("data-index")) === currentPage).classList.add("active");

                      handlePagingStatus(filtered);

                      prevButton.removeAttribute("disabled");
                      currentPage === pageAmount ? nextButton.setAttribute("disabled", "true") : nextButton.removeAttribute("disabled");
                      renderPage();
                      
                  });
              }
              if (pages)
              {
                  pages.forEach((page) => {
                      page.addEventListener("click", (e) => {
                          changePage(e, pages);
                      });
                  });
              }
          }

          function handlePagingStatus(filtered){
              let lastIndex = null;
              for(var i = (index.firstIndex); i < index.lastIndex; i++){
                  if(filtered[i] === undefined){
                      lastIndex = i;
                      break;
                  }
              }

              const pagingStatus = document.querySelector(".paging-status");
              if (pagingStatus)
              {
                  document.querySelector(".paging-status").innerText = `Showing ${index.firstIndex + 1}-${filtered.length < 9 ? filtered.length : (lastIndex !== null ? lastIndex : index.lastIndex)} of ${filtered.length}`;
              }
          }

          function init() {
              //Handle init paging status
              handlePagingStatus(filtered);
             
              //Render first page on load
              renderPage();

              //Handle page buttons
              handlePages();

              //Find Categories and Years.
              setCategories();
              setCategories2();
              setCategories3();
              setYears();


              //Add click listeners to page buttons
              pageControl();
              
              //Add change and click listeners for filtering and sorting
              sortingAndFiltering();


              //Add hover listeners to news links
              cardLinks.forEach((cardLink) => {
                  const card = cardLink.closest('.kdx-card');
                  
                  cardLink.addEventListener('mouseenter', setHover);
                  cardLink.addEventListener('mouseleave', removeHover);
                  cardLink.addEventListener('focusin', setHover);
                  cardLink.addEventListener('focusout', removeHover);
                                  
                  if (card) {
                      const cardImage = card.querySelector('.card-img-top');

                      if (!cardImage) {
                          card.classList.add('no-image');                               
                      }
                  }
              });

              var pageToNav = localStorage.getItem("newsPage");

              if (pageToNav && currentPage !== pageToNav)
              {
                  var page = document.querySelector("[data-index='" + pageToNav + "']");
                  if (page)
                  {
                      page.click();
                  }
              }
          }

          function sortNews(filteredCards){
              handlePagingStatus(filteredCards);

              currentPage = 1;
              localStorage.setItem("newsPage", 1);

              index.firstIndex = 0;
              index.lastIndex = 9;
              const cardsReplica = filteredCards.sort((a, b) => {
                  
                  //Date Sorting
                  const yearA = a?.querySelector(".date")?.textContent !== undefined && a?.querySelector(".date")?.textContent !== null ? new Date(a.querySelector(".date").textContent) : new Date("1/1/1900");
                  const yearB = b?.querySelector(".date")?.textContent !== undefined && b?.querySelector(".date")?.textContent !== null ? new Date(b.querySelector(".date").textContent) : new Date("1/1/1900");

                  //Author Sorting
                  const authorA = a?.querySelector(".author")?.textContent !== undefined && a?.querySelector(".author")?.textContent !== null ? a.querySelector(".author").textContent.toUpperCase().trim("") : " ";
                  const authorB = b?.querySelector(".author")?.textContent !== undefined && b?.querySelector(".author")?.textContent !== null ? b.querySelector(".author").textContent.toUpperCase().trim("") : " ";

                  //Title Sorting
                  const titleA = a?.querySelector(".card-title a")?.textContent !== undefined && a?.querySelector(".card-title a")?.textContent !== null ? a.querySelector(".card-title a").textContent.toUpperCase().trim("") : " ";
                  const titleB = b?.querySelector(".card-title a")?.textContent !== undefined && b?.querySelector(".card-title a")?.textContent !== null ? b.querySelector(".card-title a").textContent.toUpperCase().trim("") : " ";

                  if(activeSort === "date-descending"){
                      return yearA == yearB ? 0 : (yearA > yearB ? 1 : -1);
                  }else if(activeSort === "date-ascending"){
                      return yearA == yearB ? 0 : (yearA < yearB ? 1 : -1);
                  }else if(activeSort === "author-z-a"){
                      return authorA == authorB ? 0 : (authorA > authorB ? 1 : -1);
                  }else if(activeSort === "author-a-z"){
                      return authorA == authorB ? 0 : (authorA < authorB ? 1 : -1);
                  }else if(activeSort == "title-z-a"){
                      return titleA == titleB ? 0 : (titleA < titleB ? 1 : -1);
                  }else if(activeSort == "title-a-z"){
                      return titleA == titleB ? 0 : (titleA > titleB ? 1 : -1);
                  }
              });
              filtered = [...cardsReplica];
             
              handlePages();
              renderPage();
              pageControl();
          }
          
          function filterNews(){
                  filtered = [];
                  //Level 1 Filter (categories)
                  const cards = [...initCards];
                  cards.forEach((child) => {
                      child.remove();
                  });
                  let filteredCards = [];
                  if(activeCategory == "all"){
                      filteredCards = [...cards];
                  }else {
                      cards.forEach((card) => {
                          const cardEl = card.querySelector(".kdx-card");
                          const cardCategories = JSON.parse(cardEl.getAttribute("data-categories"));
                          if(cardCategories.includes(activeCategory)){
                              filteredCards.push(card);
                          }
                      });
                  }

                  //Level 2 Filter (categories2)
                  let filteredCards2 = [];
                  if(activeCategory2 == "all"){
                      filteredCards2 = [...filteredCards];
                  }else {
                      filteredCards.forEach((card) => {
                          const cardEl = card.querySelector(".kdx-card");
                          const cardCategories = JSON.parse(cardEl.getAttribute("data-categories-2"));
                          if(cardCategories.includes(activeCategory2)){
                              filteredCards2.push(card);
                          }
                      });
                  }

                  //Level 3 Filter (categories2)
                  let filteredCards3 = [];
                  if(activeCategory3 == "all"){
                      filteredCards3 = [...filteredCards2];
                  }else {
                      filteredCards2.forEach((card) => {
                          const cardEl = card.querySelector(".kdx-card");
                          const cardCategories = JSON.parse(cardEl.getAttribute("data-categories-3"));
                          if(cardCategories.includes(activeCategory3)){
                              filteredCards3.push(card);
                          }
                      });
                  }
        
                  //Level 4 Filter (years)
                  let filteredCards4 = [];
                  if(activeYear == "all"){
                      filteredCards4 = [...filteredCards3];
                  }else {
                      filteredCards3.forEach((card) => {
                          const cardEl = card.querySelector(".kdx-card");
                          if(cardEl?.querySelector(".date")?.textContent.slice(-4) == activeYear){
                              filteredCards4.push(card);
                          }
                      });
                  }

                  //Level 5 Filter (search term)
                  let filteredCards5 = [];
                  if(searchTerm == null){
                      filteredCards4.forEach((card) => {
                          document.querySelector(".news-listing .listing.row").append(card);
                      });
                      filteredCards5 = filteredCards4;
                  }else {
                      filteredCards4.forEach((card) => {
                          const cardEl = card.querySelector(".kdx-card");
                          if(cardEl?.querySelector(".card-title a").textContent.toUpperCase().includes(searchTerm.toUpperCase())){
                              document.querySelector(".news-listing .listing.row").append(card);
                              filteredCards5.push(card);
                          }
                      });
                  }

                  index.firstIndex = 0;
                  index.lastIndex = 9;

                  //Sort the filtered list
                  sortNews(filteredCards5);

                  //Handle pagination
                  
                  handlePages();
                  renderPage();
                  pageControl();


          }

          function setHover(e) {
              e.target.closest('.kdx-card').classList.add('hover');
          }

          function removeHover(e) {
              e.target.closest('.kdx-card').classList.remove('hover');
          }
      }
  }

  /**
   * Lazy loads images that are set up properly.
   * For img tags:        
   *      <img
              class="lazy-load-img"
              src="small-placeholder.jpeg"
  			data-src="full-sized-actual-img.jpg"
  			data-srcset="full-sized-actual-img.jpg 2x, mobile-sized-actual-img.jpg 1x"
              alt=""
          >
              
      For BG Images: <div class="lazy-load-bg" style="background-image: url('/my-image.jpeg')"></div> 

      more info: https://developers.google.com/web/fundamentals/performance/lazy-loading-guidance/images-and-video
   */
  class ImageLoader {

      constructor(options = {}) {
          this.queue = [];
          this.backgroundQueue = [];
          this.lazyImageObserver = null;
          this.lazyBgObserver = null;
          this.options = options;
      }

      initialize() {
          this.getImagesToLazyLoad();

          if (!("IntersectionObserver" in window)) {
              this.abort();
              return;
          }

          this.onContentLoaded();
          this.setGenericPlaceholder();

          this.queue.forEach((lazyImage) => {
              this.lazyImageObserver.observe(lazyImage);
          });

          this.backgroundQueue.forEach((lazyBg) => {
              this.lazyBgObserver.observe(lazyBg);
          });
      }

      getImagesToLazyLoad() {
          this.queue = document.querySelectorAll('.lazy-load-img');
          this.backgroundQueue = document.querySelectorAll('.lazy-load-bg');        
      }

      setGenericPlaceholder() {
          if (this.options.genericImagePlaceholder) {
              this.queue.forEach((img) => {
                  img.setAttribute('src', this.options.genericImagePlaceholder);
              });
          }

          if (this.options.genericBgPlaceholder) {
              this.backgroundQueue.forEach((bg) => {
                  bg.style.backgroundColor = this.options.genericBgPlaceholder;
              });
          }
      }

      onContentLoaded() {
          this.lazyImageObserver = new IntersectionObserver((entries, observer) => {
              entries.forEach((entry) => {
                  if (entry.isIntersecting) {                    
                      let lazyImage = entry.target;
                      lazyImage.src = lazyImage.dataset.src;
                      lazyImage.srcset = lazyImage.dataset.srcset;
                      lazyImage.classList.remove('lazy-load-img');
                      this.lazyImageObserver.unobserve(lazyImage);
                  }
              });
          });

          this.lazyBgObserver = new IntersectionObserver((entries, observer) => {
              entries.forEach((entry) => {
                  if (entry.isIntersecting) {
                      entry.target.classList.remove('lazy-load-bg');
                      this.lazyBgObserver.unobserve(entry.target);
                  }
              });
          });
      }

      abort() {
          for (let i = 0; i < this.queue.length; i++) {
              this.queue[i].setAttribute('src', this.queue[i].getAttribute('data-src'));
              this.queue[i].classList.remove('lazy-load-img');
          }

          for (let i = 0; i < this.backgroundQueue.length; i++) {
              this.backgroundQueue[i].classList.remove('lazy-load-bg');
          }
      }

  }

  /**
   *  Component Helpers
   */

  /**
     * Theme Helpers
     */
  Object.freeze({
      0: 'primary',
      1: 'secondary',
      2: 'tertiary',
      4: 'quarternary',
      5: 'quinary',
      6: 'senary',
      7: 'gray-lite',
      8: 'gray-dark',
  });

  /**
     * Window Helpers
     */

    // Keyboard Enum
  Object.freeze({
      'enter': 13,
      'arrow-u': 38,
      'arrow-r': 39,
      'arrow-d': 40,
      'arrow-l': 37,
      'esc': 27,
      'tab': 9,
      'space': 32
  });

  /**
     * Breakpoints are situatued such that
     * comparison would be "and down" --
     * 
     * if (window.innerWidth < _BREAKPOINTS_.sm) { ... }
     */
  const _BREAKPOINTS_ = Object.freeze({
      'xs': 576,
      'sm': 767,
      'md': 991,
      'lg': 1199,
      'xl': 1399,
      'giant': 1799
  });

  // An incomplete but convenient proxy for the current url's search parameters
  new Proxy({}, {
      get(target, property) {
          // the iterator gives an escape hatch to see order and duplicate properties
          // note that new Map() and Object.fromEntries() will use the last value in case of duplicates
          if (property === Symbol.iterator)
              return () => new URLSearchParams(location.search).entries();

          // this will return the first value in case of duplicates
          return new URLSearchParams(location.search).get(property);
      },

      set(target, property, value) {
          const url = new URL(location);
          url.searchParams.set(property, value);
          history.replaceState(undefined, undefined, url);
          return true;
      },

      deleteProperty(target, property) {
          const url = new URL(location);
          url.searchParams.delete(property);
          history.replaceState(undefined, undefined, url);
          return true;
      }
  });

  class Carousel {
      static main() {        
          // carousel options 
          let id,
              carouselType,           
              bulletsWrapper,
              totalSlides,
              perSlide,
              perSlideTablet,
              perSlideMobile,
              centerFocus,
              centerFocusMobile,
              autoPlay,
              autoPlayDuration,
              animationTimingFunc,
              hoverPause,
              peek,
              peekTablet,
              peekMobile,
              gap,
              gapTablet,
              gapMobile,
              leftArrowIcon,
              leftArrowIconMobile,
              rightArrowIcon,
              rightArrowIconMobile,
              cardsSlider,
              adjustableHeightOnMobile,
              instance,
              carouselIsMobileOnly;            

          init(document.querySelectorAll('.kdx-carousel'));

          function init(carousels) {
              if (document.body.matches('.is-editing, .sfPageEditor')) {
                  return;
              }

              window.addEventListener('resize', resizeHandler);

              carousels.forEach(carouselItem => {
                  carouselItem.id ||= `kdx-carousel-${Math.random().toString(36).slice(2)}`;

                  if (checkCarouselIsMobileOnly(carouselItem))
                  {
                      return;
                  }

                  setup(carouselItem);
              });
          }
          
          // initialize glideJS carousel
          function setup(carousel) {         
              id = carousel.id;            
              carouselType = carousel.getAttribute('data-carouselType');            
              bulletsWrapper = carousel.querySelector('.glide__bullets');
              carousel.getAttribute('data-bulletNav');            
              totalSlides = carousel.querySelector('.glide__slides').children.length;
              const glideSlides = carousel.querySelector('.glide__slides');
              const slides = [...glideSlides.children];
              perSlide = carousel.getAttribute('data-perSlide') ? carousel.getAttribute('data-perSlide') : '1';
              perSlideTablet = carousel.getAttribute('data-perSlideTablet') ? carousel.getAttribute('data-perSlideTablet') : '1';
              perSlideMobile = carousel.getAttribute('data-perSlideMobile') ? carousel.getAttribute('data-perSlideMobile') : '1';
              carousel.getAttribute('data-perSwipe');
              carousel.getAttribute('data-hideArrows');
              centerFocus = carousel.getAttribute('data-centerFocus');
              centerFocusMobile = carousel.getAttribute('data-perSwipe');
              carousel.getAttribute('data-focusAt');
              autoPlay = carousel.getAttribute('data-autoPlay');
              autoPlayDuration = carousel.getAttribute('data-autoPlayDuration') ? carousel.getAttribute('data-autoPlayDuration') : '8000';
              animationTimingFunc = carousel.getAttribute('data-animationTimingFunc') ? carousel.getAttribute('data-animationTimingFunc') : 'ease-in-out';
              hoverPause = carousel.getAttribute('data-hoverPause');
              peek = carousel.getAttribute('data-peek') ? carousel.getAttribute('data-peek') : '0';
              peekTablet = carousel.getAttribute('data-peekTablet') ? carousel.getAttribute('data-peekTablet') : '0';
              peekMobile = carousel.getAttribute('data-peekMobile') ? carousel.getAttribute('data-peekMobile') : '0';
              gap = carousel.getAttribute('data-gap') ? carousel.getAttribute('data-gap') : '32';
              gapTablet = carousel.getAttribute('data-gapTablet') ? carousel.getAttribute('data-gapTablet') : '32';
              gapMobile = carousel.getAttribute('data-gapMobile') ? carousel.getAttribute('data-gapMobile') : '16';
              carousel.getAttribute('data-pauseText');
              carousel.getAttribute('data-playText');
              carousel.getAttribute('data-rewind') ? carousel.getAttribute('data-rewind') : 'true';
              carousel.getAttribute('data-displayBulletNavExternally');
              leftArrowIcon = carousel.getAttribute('data-leftArrowIcon') ? carousel.getAttribute('data-leftArrowIcon') : 'chevron-left';
              leftArrowIconMobile = carousel.getAttribute('data-leftArrowIconMobile') ? carousel.getAttribute('data-leftArrowIcon') : 'chevron-left';
              rightArrowIcon = carousel.getAttribute('data-rightArrowIcon') ? carousel.getAttribute('data-leftArrowIcon') : 'chevron-right';
              rightArrowIconMobile = carousel.getAttribute('data-rightArrowIconMobile') ? carousel.getAttribute('data-rightArrowIconMobile') : 'chevron-right';
              cardsSlider = carousel.getAttribute('data-cardsSlider');
              carousel.getAttribute('data-fullWidth');
              adjustableHeightOnMobile = carousel.getAttribute('data-adjustableHeightOnMobile'); 
              carouselIsMobileOnly = carousel.getAttribute('data-carouselIsMobileOnly');
              window.innerWidth > _BREAKPOINTS_.sm ? leftArrowIcon : leftArrowIconMobile;
              window.innerWidth > _BREAKPOINTS_.sm ? rightArrowIcon : rightArrowIconMobile;
              
              hideOrShowArrows(carousel);
              setFullBackground(carousel);
              setCarouselButtonPosition(carousel);


              let options = {
                  type: carouselType ? carouselType : 'carousel',
                  perView: parseInt(perSlide, 10),
                  perSwipe: '',
                  focusAt: centerFocus ? 'center' : '',
                  autoplay: autoPlay ? parseInt(autoPlayDuration, 10) : 0,
                  animationTimingFunc: animationTimingFunc,
                  hoverpause: hoverPause,
                  peek: peek,
                  gap: gap,
                  rewind: false,
                  dragThreshold: (totalSlides > perSlide) ? true : false,                    
                  breakpoints: {
                      980: {
                          perView: parseInt(perSlideTablet, 10),
                          peek: peekTablet,
                          gap: gapTablet,
                          dragThreshold: (totalSlides > perSlideTablet) ? true : false
                      },
                      720: {
                          perView: parseInt(perSlideMobile, 10),
                          peek: peekMobile,
                          gap: gapMobile,
                          focusAt: centerFocusMobile ? 'center' : '',
                          dragThreshold: (totalSlides > perSlideMobile) ? true : false
                      },
                  },
              };

              // Fix keyboard navigation
              const track = glideSlides.parentElement;
              track.addEventListener('focusin', ()=> {
                  let unscrolled = track.scrollLeft === 0;
                  track.scrollLeft = 0;
                  // webkit scrolls in the next task
                  setTimeout(() => {
                      if (track.scrollLeft === 0 && unscrolled)
                          return;
                      track.scrollLeft = 0;
                      const slide = e.target.closest('.glide__slide');
                      const slideIndex = slides.indexOf(slide);
                      // could be a clone slide
                      if (slideIndex !== -1)
                          this.slider.go(`=${slideIndex}`);
                  }, 0);
              });

              if (bulletsWrapper) {
                  for (let i = 0; i < totalSlides; i++) {
                      bulletsWrapper.innerHTML += `<button class="glide__bullet" data-glide-dir="=${i}" aria-label="Go to slide ${i + 1}" aria-controls=${id + '-slides'}></button>`;
                  }
              }

              if (cardsSlider) {
                  setCountText();

                  instance.on(['run.after', 'resize'], () => {
                      setCountText();
                      hideOrShowArrows(carousel);

                      const isMobile = window.innerWidth > _BREAKPOINTS_.sm ? false : true;

                      if (carouselIsMobileOnly && !isMobile)
                      {
                          this.destroy();
                      }
                  });
              }
              
              if (perSlide) {                
                  instance = new Glide(`#${id}`, options).mount();
              } else {
                  instance = new Glide(`#${id}`, {
                      autoplay: autoPlay ? parseInt(autoPlayDuration, 10) : null,
                      hoverpause: hoverPause
                  }).mount();
              }

              //FullCarouselButtonConfig(carousel, instance);

              // adjustable height.  Change height to active slide height, and scrolls to top of slide.  good for mobile with variable height slides
              if (adjustableHeightOnMobile && window.innerWidth <= _BREAKPOINTS_.md) {

                  instance.on('build.after', ()=> {
                      let slideHeight = root.querySelector('.glide__slide--active .row').clientHeight;
                      let glideTrack = root.querySelector('.glide__track').clientHeight;
                      
                      if (slideHeight != glideTrack) {
                          let newHeight = slideHeight;
                          carousel.querySelector('.glide__track').style.height = newHeight+'px';
                          carousel.querySelector('.glide__track').scrollIntoView({behavior: "smooth", block: "start"});
                      }
                  });
                  
                  instance.on('run.after', ()=> {
                      let slideHeight = carousel.querySelector('.glide__slide--active .row').clientHeight;
                      let glideTrack = carousel.querySelector('.glide__track').clientHeight;
                      
                      if (slideHeight != glideTrack) {
                          let newHeight = slideHeight;
                          carousel.querySelector('.glide__track').style.height = newHeight+'px';
                          carousel.querySelector('.glide__track').scrollIntoView({behavior: "smooth", block: "start"});
                      }
                  });
              }

              instance.on(['run.after', 'resize'], () => {
                  let isMobile = window.innerWidth > _BREAKPOINTS_.sm ? false : true;

                  if (carouselIsMobileOnly)
                  {
                      if (!isMobile) {
                          instance.destroy();
                      }
                  }
                  window.innerWidth > _BREAKPOINTS_.sm ? leftArrowIcon : leftArrowIconMobile;
                  window.innerWidth > _BREAKPOINTS_.sm ? rightArrowIcon : rightArrowIconMobile;

                  let carousels = document.querySelectorAll('.kdx-carousel');

                  carousels.forEach(carouselItem => {
                      setCarouselButtonPosition(carousel);    
                  });
              });

              instance.on('mount.after', () => {
                  if (ImageLoader != null) {
                      ImageLoader.initialize();
                  }           
              });            
          }

          function hideOrShowArrows(carousel) {            
              if (carousel) {
                  if (window.innerWidth >= 981) {
                      if (totalSlides <= parseInt(perSlide, 10)) {
                          carousel.classList.add('hide-arrows');
                      }
                      else {
                          carousel.classList.remove('hide-arrows');
                      }
                  }
      
                  if (window.innerWidth <= 980 && window.innerWidth >= 721) {
                      if (totalSlides <= parseInt(perSlideTablet, 10)) {
                          carousel.classList.add('hide-arrows');
                      }
                      else {
                          carousel.classList.remove('hide-arrows');
                      }
                  }
      
                  if (window.innerWidth <= 720) {
                      if (totalSlides <= parseInt(perSlideMobile, 10)) {
                          carousel.classList.add('hide-arrows');
                      }
                      else {
                          carousel.classList.remove('hide-arrows');
                      }
                  }
              }
          }

          function setFullBackground(carousel) {
              if (carousel)
              {
                  const isFullCarousel = carousel.parentElement.classList.contains('multi-card-carousel-full');

                  if (isFullCarousel)
                  {
                      const fullCarousel = carousel.parentElement;
                      //get block data
                      const backColor = carousel.parentElement.querySelector('.card-header')?.getAttribute('data-bg-color');
                      const backImage = carousel.parentElement.querySelector('.card-header')?.getAttribute('data-bg-img');
                      const showDots = carousel.parentElement.querySelector('.card-header')?.getAttribute('data-show-dots');
                      const dots = carousel.querySelector('.glide__bullets');
                      const prevNext = carousel.querySelector('.glide__arrows');
                      const showPrevNext = carousel.parentElement.querySelector('.card-header')?.getAttribute('data-show-prevnext');

                      if ((showDots != 'True') && (showPrevNext != "True")) {
                          prevNext.style.display = 'none';
                      }
                      else if (showDots != 'True') {
                          dots.style.display = 'none';
                      }
                      else if (showPrevNext != 'True') {
                          prevNext.style.display = 'none';
                      }

                      if (backImage && backImage !== "")
                      {
                          fullCarousel.style.backgroundImage = "url('" + backImage + "')";
                      }
                      else if (backColor && backColor !== "")
                      {
                          fullCarousel.classList.add(backColor);
                      }
                  }
              }
          }

          function setCarouselButtonPosition(carousel)
          {
              if (carousel)
              {
                  const isGalleryCarousel = carousel.parentElement.classList.contains('image-gallery-carousel');

                  if (isGalleryCarousel)
                  {
                      let gallerySlideContainer = carousel.querySelector('.glide__slide--active ');
                      if (!gallerySlideContainer)
                      {
                          gallerySlideContainer = carousel.querySelector('.glide__slide');
                      }

                      if (gallerySlideContainer)
                      {
                          var photoHeight = gallerySlideContainer.querySelector('.photo')?.height;
                          if (photoHeight)
                          {
                              carousel.querySelector('.glide__arrow--left').style.height = photoHeight + 'px';
                              carousel.querySelector('.glide__arrow--right').style.height = photoHeight + 'px';
                          }
                      }
                  }
              }
          }

          function checkCarouselIsMobileOnly(carousel) {
              const carouselIsMobileOnly = carousel.getAttribute('data-carouselIsMobileOnly') ?? false;    

              let isMobile = window.innerWidth > _BREAKPOINTS_.sm ? false : true;
              return (carouselIsMobileOnly && !isMobile) 
          }

          function resizeHandler() {
              let carousels = document.querySelectorAll('.kdx-carousel');

              carousels.forEach(carouselItem => {
                  const carouselIsMobileOnly = carouselItem.getAttribute('data-carouselIsMobileOnly') ?? false;   

                  if (carouselIsMobileOnly)
                  {
                      let isMobile = window.innerWidth > _BREAKPOINTS_.sm ? false : true;
                      if (isMobile)
                      {
                          setup(carouselItem);
                      }
                  }
              });
          }
      }
  }

  class Datatables {
      static main() {
          const tables = document.querySelectorAll('.dataTable');
          tables.forEach((table, index) => {           
              init(table, index);
          });

          function init(table, index) {
              const id = table.getAttribute('id') ? table.getAttribute('id') : 'table-'+index+'';
              if (!table.getAttribute('id'))
              {
                  table.setAttribute('id', id);
              }
              const paging = table.getAttribute('data-paging') === "false" ? false : true;
              const info = table.getAttribute('data-info') === "false" ? false : true;
              const searching = table.getAttribute('data-searching') === "false" ? false : true;
              const isResponsive = table.getAttribute('data-responsive') === "true" ? true : false;
              const horizontalScrollbar = table.getAttribute('data-horizontal-scroll') === "true" ? true : false;
              const orderCol = table.getAttribute('data-order-col') ?? 0;
              const orderDir = table.getAttribute('data-order-direction') ?? 'asc';
              const orderArray = JSON.parse('[[' + orderCol + ', "' +  orderDir + '"]]');
              
              $('#'+id+'').DataTable({
                  "paging": paging,
                  "order": orderArray,
                  "info": info,
                  "searching": searching,
                  "scrollX": horizontalScrollbar,
                  "columnDefs": [{ targets: 'sorting_disabled', orderable: false }],
                  responsive: isResponsive, drawCallback: function (settings) {
                      var pagination = $(this).closest('.dataTables_wrapper').find('.dataTables_paginate');
                      pagination.toggle(this.api().page.info().pages > 1);
                  }
              });
          }
      }
  }

  class Footer {
      static main() {

          init();        
          
          function init() {
              const currentYear = document.querySelector('footer.site-footer .current-year');
              if (currentYear) {
                  getCurrentYear();
              }

          }

          function getCurrentYear() {
              const year = new Date().getFullYear();
              const yearHTML = document.querySelector('footer.site-footer .current-year');
              yearHTML.innerHTML = year.toString();
          }
      }
  }

  class FeaturedImageVideoCallout {
      static main() {        
          const card = document.querySelector('.featured-image-video-callout');        
          
          if (card) {
              init();
          }      
          
          function init() {
              calculateHeaderHeights();

              window.addEventListener('resize', ()=> {
                  calculateHeaderHeights();
              });            
          }

          // loop thru all card headlines and set equal heights
          function calculateHeaderHeights() {
              const cardLayouts = document.querySelectorAll('.card-layout');

              cardLayouts.forEach((cardLayout) => {
                  const cardHeadlines = cardLayout.querySelectorAll('.featured-image-video-callout .headline');
                  let maxHeight = 0;

                  cardHeadlines.forEach((headline) => {
                      headline.style.height = "auto";
                      maxHeight = headline.clientHeight > maxHeight ? headline.clientHeight : maxHeight;
                  });
                  // now set each headline to tallest
                  cardHeadlines.forEach((headline) => {
                      headline.style.height = maxHeight+'px';
                  });
              });
          }
      }
  }

  class heroBanner {
      static main() {

          const pausePlayBtn = document.querySelector('.hero-banner .pause-play-video');

          if (pausePlayBtn) {
              pausePlayBtn.addEventListener('click', pausePlayVideo);
          }

          function pausePlayVideo() {
              const video = document.querySelector('.hero-banner .video');
              if (video.paused) {
                  video.play();
                  pausePlayBtn.setAttribute('aria-label', 'Video is playing.  Click to pause.');
              } else {
                  video.pause();
                  pausePlayBtn.setAttribute('aria-label', 'Video is paused.  Click to play.');
              }
          }
      }
  }

  class IconCard {
      static main() {

          const iconCards = document.querySelectorAll('.icon-card a.card-link');
          
          if (iconCards) {
              init();
          }

          function init() {
              iconCards.forEach((card) => {
                  card.addEventListener('mouseenter', setHover);
                  card.addEventListener('mouseleave', removeHover);
                  card.addEventListener('focusin', setHover);
                  card.addEventListener('focusout', removeHover);                
              });
          }

          function setHover(e) {
              e.target.closest('.icon-card').classList.add('hover');
          }

          function removeHover(e) {
              e.target.closest('.icon-card').classList.remove('hover');
          }
      }
  }

  class ImageCard {
      static main() {

          const imageCards = document.querySelectorAll('.image-card a');
          init();

          function init() {
              imageCards.forEach((card) => {
                  card.addEventListener('mouseenter', setHover);
                  card.addEventListener('mouseleave', removeHover);
                  card.addEventListener('focusin', setHover);
                  card.addEventListener('focusout', removeHover);
                  
                  const href = card.getAttribute('href');
                  const urlExtension = getUrlExtension(href);
                  
                  let fileExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'mp4', 'txt', 'zip', 'jpg', 'png', 'gif'];

                  if (fileExtensions.includes(urlExtension)) {
                      card.classList.add('has-doc-file');
                  }
              });
          }

          function setHover(e) {
              e.target.closest('.image-card').classList.add('hover');
          }

          function removeHover(e) {
              e.target.closest('.image-card').classList.remove('hover');
          }

          function getUrlExtension( url ) {
              var endUrl = url.split(/[#?]/)[0].split('.').pop().trim();
              return(endUrl);
          }
      }
  }

  class LeftNavMenu {
      static main() {
          const seriius = document.querySelector('body.seriius');
          const gov = document.querySelector('body.gov');

          const leftNav = document.querySelector('.left-nav-menu');        
          const shareBarCol9 = document.querySelector('.force-tools-col9');   
          document.querySelectorAll('.left-nav-menu .parent a');      
          const toggleLeftNavBtns = document.querySelectorAll('body.theSource .toggle-left-nav');

          let browserWidth = window.innerWidth;  
                  
          // only move leftnav into header if on seriius site, since seriius doesn't have a main menu
          if (leftNav && seriius) {
              init();
          }

          if ((leftNav || shareBarCol9) && gov) {
              moveShareBar();

              window.addEventListener('resize', resizeHandler);
          }
          
          // on theSource the leftnav turns into mobile accordion
          toggleLeftNavBtns.forEach((toggleLeftNavBtn) => { 
              toggleLeftNavBtn.addEventListener('click', (e) => { toggleLeftNav(e);});
          });
          
          function init() {
              const seriius = document.querySelector('body.seriius');
              const leftnav = document.querySelector('.left-nav-menu');    

              if (leftnav && seriius)
              {
                  moveLeftNav();
                  bindMobile();
              }

              window.addEventListener('resize', resizeHandler);
          }

          function resizeHandler() {
              // only move leftNav if browser actually resized in width and not just ios triggering a resize on menu opens             
              const seriius = document.querySelector('body.seriius');
              const gov = document.querySelector('body.gov');
              const leftnav = document.querySelector('.left-nav-menu');    

              if (browserWidth != window.innerWidth) {
                  if (leftnav && seriius) {
                      moveLeftNav();
                  }

                  if (leftnav && gov) {
                      moveShareBar();
                  }

                  browserWidth = window.innerWidth;
              }
          }

          function moveLeftNav() {
              // on Seriius site, the left nav needs to be moved for mobile
              // determine if on mobile or desktop menu and move leftNav accordingly in DOM
              const leftNavMobileWrapper = document.querySelector('.left-nav-mobile-wrapper');
              const leftNavDesktopWrapper = document.querySelector('.left-nav-desktop-wrapper');

              // desktop menu
              if (window.innerWidth >= 992) {
                  if (leftNav && leftNavMobileWrapper) {
                      leftNavDesktopWrapper.append(leftNav);
                  }
              }
              // mobile menu
              else {
                  leftNavMobileWrapper.append(leftNav);

                  const parentNodes = leftNavMobileWrapper.querySelectorAll('.left-nav-menu .node');
                  if (parentNodes) {
                      parentNodes.forEach((node) => {
                          // close all mobile menu's except active-page
                          const toggleIcon = node.querySelector('.parent-toggle svg use');
                          
                          if (toggleIcon) {
                              node.classList.remove('open');
                              let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#plus');
                              toggleIcon.setAttribute('href', iconHref);
                          }
                      });
                  }
              }
              document.querySelector('body').classList.remove('menu-open');
              document.querySelector('.mobile-nav-toggle').classList.remove('opened');
              bindMobile();
          }

          function moveShareBar() {

                  if ((leftNav || shareBarCol9) && gov && window.innerWidth >= 992) {
                      {    
                          const shareDiv = document.getElementById("page-tools");
                          if (shareDiv)
                          {
                              shareDiv.classList.remove("col-12");
                              shareDiv.classList.add("col-9");
                          }
                      }

                  }
                  else if ((leftNav || shareBarCol9) && gov && window.innerWidth < 992) {
                      const shareDiv = document.getElementById("page-tools");
                      if (shareDiv)
                      {
                          shareDiv.classList.remove("col-9");
                          shareDiv.classList.add("col-12");
                      }
                  }
              
          }   

          // Bind mobile mouse/keyboard interactions
          function bindMobile() {
              const mobileNavToggle = document.querySelector('.mobile-nav-toggle');
              const parentToggles = document.querySelectorAll('.left-nav-menu .parent-toggle');
              const parentNodes = document.querySelectorAll('.left-nav-menu .node .parent a');

              if (mobileNavToggle) {
                  mobileNavToggle.addEventListener('click', toggleMobileMenu);
              }

              if (parentToggles) {
                  parentToggles.forEach((parentToggle) => {
                      parentToggle.addEventListener('click', openMobileSubmenu);
                      parentToggle.addEventListener('keyup', openMobileSubmenu);
                  });
              }

              parentNodes.forEach((parentNode) => {
                  // now bind
                  parentNode.addEventListener('focusin', closeAllMobileSubmenus);
              }); 
          }

          function toggleMobileMenu(e) {
              e.preventDefault();
              
              e.currentTarget.classList.toggle('opened');
              document.querySelector('body').classList.toggle('menu-open');
          }

          // open mobile submenu
          function openMobileSubmenu(e) {
              const openNodes = document.querySelectorAll('.left-nav-menu .node.open');
              const keyCode = e.key;
                          
              // close any open menus except clicked one
              if (openNodes) {
                  openNodes.forEach((node) => {
                      if (node != e.currentTarget.closest('.node')) {
                          const toggleIcon = node.querySelector('.parent .parent-toggle svg use');

                          node.closest('.node').classList.remove('open');
                          if (toggleIcon) {
                              let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#plus');
                              toggleIcon.setAttribute('href', iconHref);
                          }

                      }
                  });
              }
              // toggle subnav if mouse click or enter/down key pressed
              if (keyCode === undefined || keyCode == "Enter" || keyCode == "ArrowDown") {
                  const toggleIcon = e.currentTarget.querySelector('svg use');

                  if (!e.currentTarget.closest('.node').classList.contains('open')) {
                      e.currentTarget.closest('.node').classList.add('open');
                      
                      if (toggleIcon) {
                          let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#minus');
                          toggleIcon.setAttribute('href', iconHref);
                      }
                  }
                  else {
                      e.currentTarget.closest('.node').classList.remove('open');
                      
                      if (toggleIcon) {
                          let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#plus');
                          toggleIcon.setAttribute('href', iconHref);
                      }
                  }
              }
          }

          function closeAllMobileSubmenus(e) {            
              if (!e.currentTarget.classList.contains('open')) {
                  const openNodes = document.querySelectorAll('.left-nav-menu .node.open');
                  
                  if (openNodes) {
                      openNodes.forEach((node) => {
                          const toggleIcon = node.querySelector('.parent .parent-toggle svg use');

                          node.classList.remove('open');
                          if (toggleIcon) {
                              let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#plus');
                              toggleIcon.setAttribute('href', iconHref);
                          }
                      });
                  }                
              }
          }

          function toggleLeftNav(e) {
              e.preventDefault();
              const leftNavMenu = e.currentTarget.closest('.toggle-left-nav-wrapper').nextElementSibling;
                          
              if (leftNavMenu) {
                  if (!leftNavMenu.classList.contains('show')) {
                      leftNavMenu.classList.add('show');
                      e.currentTarget.setAttribute('aria-expanded', true);
                  }
                  else {
                      leftNavMenu.classList.remove('show');
                      e.currentTarget.setAttribute('aria-expanded', false);
                  }
              }
          }
      }
  }

  class LinkUpdates {
      static main() {
          const bodyClass = document.body.classList;

          // only run for GOV/theSource
          if (bodyClass.contains("gov") || bodyClass.contains("theSource")) {
              const links = document.querySelectorAll("a");

              links.forEach(link => {
                  const href = link.getAttribute("href");
                  if (href) {
                      removeTitleAttribute(link, href);
                  }
              });
          }

          // CMS adds title to any file links, so this removes the title attribute
          function removeTitleAttribute(link, href) {
              const fileTypes = ["pdf", "doc", "xls", "ppt", "mp4", "txt", "zip"];
              const fileExtension = href.split('.').pop().toLowerCase();

              // Check if the file extension ends with any of the specified file types
              if (fileTypes.some(type => fileExtension.includes(type))) {
                  if (link.hasAttribute("title")) {
                      link.removeAttribute("title");
                  }
              }
          }
      }
  }

  class LogosWidget {
      static main() {
          const backgroundImg = document.querySelector('.logos-widget .background-img');
          let useImg = "";

          if (backgroundImg) {
              init();
          }

          function init() {
              setTimeout(function () {
                  setBG();
              }, 500);
              window.addEventListener('resize', setBG);
          }

          // determines if on desktop or mobile and changes image accordingly
          function setBG() {
              const desktopImg = backgroundImg.getAttribute('data-desktop-img');
              const mobileImg = backgroundImg.getAttribute('data-mobile-img');

              if (desktopImg && backgroundImg) {
                  useImg = (!mobileImg || window.innerWidth >= _BREAKPOINTS_.sm) ? desktopImg : mobileImg;

                  backgroundImg.setAttribute('src', useImg);
                  backgroundImg.setAttribute('srcset', useImg);
              }

          }
      }
  }

  class MainMenu {
      static main() {
          const parentLinks = document.querySelectorAll('.main-menu .parent a');
          const bodyClasses = document.querySelector('body').classList;
          var timeId = null;

          window.addEventListener('resize', () => {
              clearTimeout(timeId);
              timeId = setTimeout(executeAfterResize, 300);
           });

           function executeAfterResize() {
              setBindings();
              moveSearch();
              userLinksMobile();
              checkStacked();
           }

          let siteMenu;
          let abortControl = undefined; // used for binding/unbinding 
          
          // microsites use hover, DOE/GOV uses clicks. Microsites and GOV have + and - for mobile menu where DOE has carets    
          if (bodyClasses.contains('microsite')) {
              siteMenu = 'microsite';
          }
          else if (bodyClasses.contains('doe')) {
              siteMenu = 'doe';
          }
          else if (bodyClasses.contains('gov')) {
              siteMenu = 'gov';
          }        
          else if (bodyClasses.contains('theSource')) {
              siteMenu = 'theSource';
          }        
          
          if (parentLinks.length) {
              init();
          }
          else {
              noMenu(); // remove menu/mobile toggle
          }
          
          function init() {            
              setBindings();
              moveSearch();
              userLinksMobile();
              checkStacked();
              //addMargin();
              
              window.addEventListener('resize', setBindings);
              document.addEventListener('click', checkIfOutsideOfMenu);
              document.addEventListener('keyup', checkIfOutsideOfMenu);
              window.addEventListener('resize', moveSearch);
              window.addEventListener('resize', userLinksMobile);

              //window.addEventListener('resize', addMargin);
              window.addEventListener('resize', hideShowSearchheader2);
              window.addEventListener('resize', hideShowSearchheader1);
              window.addEventListener('resize', hideShowSearchheader3);
              reformatRequired();
          }        

          function setBindings() {
              // unbind event listeners before adding them again
              abortControl?.abort();
              abortControl = new AbortController; 
              
              const desktop = matchMedia('(min-width: 1200px)');
              if (desktop.matches)
                  bindDesktop();
              else
                  bindMobile();

              const navStyle = document.getElementById('navStyle');
              if (navStyle && navStyle.value == 'gray-background') {
                  document.querySelector('.navbar-row').classList.add('gray-background');
              }

              desktop.addEventListener('change', () => {
                  if (desktop.matches)
                      bindDesktop();
                  else
                      bindMobile();
              });
          }
          
          // Bind desktop for mouse/keyboard interactions
          function bindDesktop() {
              // open dropdowns on click
              if (siteMenu === 'doe' || siteMenu === 'gov' || siteMenu === 'microsite' || siteMenu === 'theSource') {
                  parentLinks.forEach((parentLink) => {       
                      var children = parentLink.closest('.node').querySelector('.children');

                      if (children) {
                          parentLink.addEventListener('click', openSubmenu, { signal: abortControl.signal });
                      }    
                  });
              }
          }

          // Bind mobile mouse/keyboard interactions
          function bindMobile() {
              const mobileNavToggle = document.querySelectorAll('.mobile-nav-toggle');
              const parentToggles = document.querySelectorAll('.main-menu .parent-toggle');
              const childToggles = document.querySelectorAll('.main-menu .child-toggle');
              const parentLinks = document.querySelectorAll('.main-menu .node .parent a');
              
              mobileNavToggle.forEach((toggle) => {                           
                  toggle.addEventListener('click', toggleMobileMenu, { signal: abortControl.signal });
              });

              parentToggles.forEach((parentToggle) => {
                  parentToggle.addEventListener('click', openMobileSubmenu, { signal: abortControl.signal });
                  parentToggle.addEventListener('keyup', openMobileSubmenu, { signal: abortControl.signal });
              });

              childToggles.forEach((childToggle) => {
                  childToggle.addEventListener('click', openMobileSubmenu, { signal: abortControl.signal });
                  childToggle.addEventListener('keyup', openMobileSubmenu, { signal: abortControl.signal });
              });

              parentLinks.forEach((parentLink) => {
                  const href = parentLink.getAttribute('href');

                  if (href === "#")
                  {
                      parentLink.addEventListener('click', openMobileSubmenu, { signal: abortControl.signal });
                      parentLink.addEventListener('keyup', openMobileSubmenu, { signal: abortControl.signal });
                  }
                  else
                  {
                      parentLink.addEventListener('focusin', closeAllMobileSubmenus, { signal: abortControl.signal });
                  }
              });
              
              // for GOV, hide header3 search on page load even if set to show by default on desktop
              const header3SearchWrappers = document.querySelectorAll('.header3 .search-menu-wrapper');         
              header3SearchWrappers.forEach((header3SearchWrapper) => {                
                  header3SearchWrapper.classList.add('hide');

                  var searchToggle = document.querySelector('.toggle-header3-search');
                  if (searchToggle.style.display == "none")
                  {
                      header3SearchWrapper.classList.remove('hide');
                  }
              });

              // for GOV, hide header2 search on page load even if set to show by default on desktop
              const header2SearchWrappers = document.querySelectorAll('.header2 .mobile-search-menu-wrapper');
              header2SearchWrappers.forEach(function (header2SearchWrapper) {
                  header2SearchWrapper.classList.add('hide');
              });
          }

          function hideShowSearchheader3() {
              const header3 = document.querySelector('.header3');
              if(header3) {
                  const header3SearchWrappers = document.querySelectorAll('.header3 .search-menu-wrapper');
                  const mobileSearchSpacer = document.querySelector('.mobile-search-spacer');
                  header3SearchWrappers.forEach((header3SearchWrapper) => {    
                      if(window.innerWidth <= 991.98 && bodyClasses.contains('search-open')) {
                          header3SearchWrapper.classList.add('hide');
                          mobileSearchSpacer.style.display = 'none';
                          bodyClasses.remove('search-open');
                      } else if(window.innerWidth >= 991 && bodyClasses.contains('search-open')) {
                          header3SearchWrapper.classList.remove('hide');
                          mobileSearchSpacer.style.display = 'block';
                          bodyClasses.add('search-open');
                      }           
                  });
              }
          }
          function hideShowSearchheader2() {
              const header2SearchWrappers = document.querySelectorAll('.header2 .search-menu-wrapper');
              header2SearchWrappers.forEach((header2SearchWrapper) => {    
                  if(window.innerWidth <= 991.98) {
                      header2SearchWrapper.classList.add('hide');

                  } else if(window.innerWidth >= 991) {
                      header2SearchWrapper.classList.remove('hide');

                  }           
              });
          }
          function hideShowSearchheader1() {
              const header1SearchWrappers = document.querySelectorAll('.header1 .search-menu-wrapper');
              const headerOneSearch = document.querySelector('.header1 .header-search');
              header1SearchWrappers.forEach((header1SearchWrapper) => {    
                  if(window.innerWidth <= 991.98) {
                      header1SearchWrapper.classList.add('hide');
                      headerOneSearch.style.display = 'none';

                  } else if(window.innerWidth >= 991) {
                      header1SearchWrapper.classList.remove('hide');
                      headerOneSearch.style.display = 'block';

                  }           
              });
          }

            // GOV header3 with userLinks, add to ul on mobile menu
          function userLinksMobile() {
              const header = document.querySelector('.gov-site-header.header3');

              if (header)
              {
                  const dropdownList = header.querySelector('.main-menu ul');
                  const userLinks = header.querySelector('.navbar-row .user-action-links');
      
                  if(window.innerWidth < 992) {
                      if(userLinks) {
                          dropdownList.appendChild(userLinks);
                      }
                  }
                  else {
                      const menuWrapper = header.querySelector('.menu-wrapper');

                      if (menuWrapper)
                      {
                          if (userLinks && !menuWrapper.querySelector('user-action-links'))
                          {
                              menuWrapper.appendChild(userLinks);
                          }
                      }
                  }
              }
          }

          function checkStacked() {
              const windowWidth = window.innerWidth;
              
              const isheader2 = document.querySelector('.header2');

              //don't care about mobile, but for desktop, check if we need to stack the menu
              if (isheader2 && windowWidth && windowWidth > 992)
              {
                  const grayBackground = document.querySelector('.gray-background');
                  if (grayBackground)
                  {
                      grayBackground.classList.remove('is-stacked');

                      const titleWidth = document.querySelector('.brand-title')?.offsetWidth;
                      const menuWidth = document.querySelector('.menu-col')?.offsetWidth;
                      const menuHeight = document.querySelector('.gray-background .container .row')?.offsetHeight;
                      

                      if (windowWidth && titleWidth && menuWidth && menuHeight) {
                          if (menuHeight > 80 || (windowWidth - (titleWidth + menuWidth + 40) < 0))
                          {
                              if (grayBackground) {
                                  grayBackground.classList.add('is-stacked');
                              }
                          }
                      }
                  }
              }
          }

          // open desktop submenu
          function openSubmenu(e) {
              e.preventDefault();
              const openNodes = document.querySelectorAll('.main-menu .node.open');            
              
              // close any open menus except clicked one
              openNodes.forEach((node) => {
                  if (node != e.currentTarget.closest('.node')) {                    
                      node.closest('.node').classList.remove('open');
                      document.querySelector('body').classList.remove('menu-open');
                  }
              });
              e.currentTarget.closest('.node').classList.toggle('open');            
              document.querySelector('body').classList.toggle('menu-open');              
          }
          
          // check if clicked outside of any menu, if so close all submenus
          function checkIfOutsideOfMenu(e) {            
              const activeElement = document.activeElement;
              // const menu = document.querySelector('.main-menu');
              // const programMenu = document.querySelector('.program-menu');
              // const mobileMenuBtn = document.querySelector('.mobile-nav-toggle');
              const openNode = document.querySelector('.main-menu .node.open');

              if (!e.target.closest('.main-menu') && !e.target.closest('.mobile-nav-toggle')) {
                  closeAllSubmenus();
                  document.querySelector('body').classList.remove('menu-open');
              }

              // if keyboard focuses into a parent node, close any open nodes
              if (openNode && activeElement.closest('.node')) {
                  if (openNode != activeElement.closest('.node')) {                    
                      openNode.classList.remove('open');
                  }
              }
          }

          function closeAllSubmenus() {
              const openNodes = document.querySelectorAll('.main-menu .node.open');
              const mobileNavToggle = document.querySelector('.mobile-nav-toggle');
              if (openNodes) {
                  openNodes.forEach((node) => {
                      node.classList.remove('open');
                  });
              }
              document.querySelector('body').classList.remove('menu-open');
              if (mobileNavToggle) {
                  mobileNavToggle.classList.remove('opened');
              }
          }

          function toggleMobileMenu(e) {            
              e.preventDefault();
              const searchToggleBtn = document.querySelector('.toggle-search');
              const mobileSearch = document.querySelector('.mobile-search-menu-wrapper');
              const searchMenuWrap = document.querySelector('.search-menu-wrapper');
              const mobileSearchSpacer = document.querySelector('.mobile-search-spacer');
              
              if (searchToggleBtn) {
                  document.querySelector('body').classList.remove('search-open');
                  searchToggleBtn.classList.remove('show');
                  if(mobileSearch) {
                      mobileSearch.classList.add('hide');

                  } else if(searchMenuWrap) {
                      searchMenuWrap.classList.add('hide');
                  }


              } else if(searchToggleBtn && window.innerWidth <= 991.98 && searchToggleBtn.classList.conatains('show')) {
                  mobileSearch.classList.remove('hide');
              }
              
              e.currentTarget.classList.toggle('opened');
              document.querySelector('body').classList.toggle('menu-open');
              if (mobileSearchSpacer)
              {
                  mobileSearchSpacer.style.display = 'none';
              }
          }

          // open mobile submenu
          function openMobileSubmenu(e) {
              e.preventDefault();        
              const openNodes = document.querySelectorAll('.main-menu .node.open');
              const openChildNodes = document.querySelectorAll('.main-menu .child-node.open');            
              let currentNode = e.currentTarget.classList.contains('parent-toggle') ? e.currentTarget.closest('.node') : e.currentTarget.closest('.child-node');
              if (currentNode == null)
              {
                  currentNode = e.currentTarget.closest('.node');
              }
              const keyCode = e.key;
                          
              // if clicking parent toggle, close any open menus except clicked one.  if child toggle clicked ignore
              if (openNodes && !e.currentTarget.classList.contains('child-toggle')) {
                  openNodes.forEach((node) => {
                      if (node != e.currentTarget.closest('.node')) {
                          const toggleIcon = node.querySelector('.parent .parent-toggle svg use');

                          node.closest('.node').classList.remove('open');
                          // Microsites & GOV mobile menu uses + and - icons
                          if (toggleIcon && (siteMenu == 'microsite' || siteMenu == 'theSource')) {
                              // header3 on GOV doesn't change icons
                              if (!e.target.closest('header.header3') && !e.target.closest('header.header2')) {
                                  let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#plus');
                                  toggleIcon.setAttribute('href', iconHref);
                              }
                          }

                          if (toggleIcon && siteMenu == 'gov') {
                              // header3 on GOV doesn't change icons
                              if (!e.target.closest('header.header3') && !e.target.closest('header.header2')) {
                                  let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#chevron-down');
                                  toggleIcon.setAttribute('href', iconHref);
                              }
                          }

                      }
                  });
              }
              else {
                  openChildNodes.forEach((node) => {
                      if (node != e.currentTarget.closest('.child-node')) {
                          const toggleIcon = node.querySelector('.children .child-toggle svg use');

                          node.closest('.child-node').classList.remove('open');
                          // Microsites & GOV mobile menu uses + and - icons
                          if (toggleIcon && (siteMenu == 'microsite' || siteMenu === 'theSource')) {
                              // header3 on GOV doesn't change icons
                              if (!e.target.closest('header.header3') && !e.target.closest('header.header2')) {
                                  let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#plus');
                                  toggleIcon.setAttribute('href', iconHref);
                              }
                          }

                          if (toggleIcon && siteMenu == 'gov') {
                              // header3 on GOV doesn't change icons
                              if (!e.target.closest('header.header3') && !e.target.closest('header.header2')) {
                                  let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#chevron-down');
                                  toggleIcon.setAttribute('href', iconHref);
                              }
                          }

                      }
                  });
              }

              // toggle subnav if mouse click or enter/down key pressed
              if (keyCode === undefined || keyCode == "Enter" || keyCode == "ArrowDown") {
                  const toggleIcon = e.currentTarget.querySelector('svg use');

                  if (!currentNode.classList.contains('open')) {
                      currentNode.classList.add('open');
                      
                      // Microsites & GOV mobile menu uses + and - icons                    
                      if (toggleIcon && (siteMenu === 'microsite' || siteMenu === 'theSource')) {
                          // header3 on GOV doesn't change icons
                          if (!e.target.closest('header.header3') && !e.target.closest('header.header2')) {
                              let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#minus');
                              toggleIcon.setAttribute('href', iconHref);
                          }
                      }

                      if (toggleIcon && siteMenu == 'gov') {
                          // header3 on GOV doesn't change icons
                          if (!e.target.closest('header.header3') && !e.target.closest('header.header2')) {
                              let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#chevron-up');
                              toggleIcon.setAttribute('href', iconHref);
                          }
                      }
                  }
                  else {                    
                      currentNode.classList.remove('open');
                      
                      // Microsites & GOV mobile menu uses + and - icons
                      if (toggleIcon && (siteMenu === 'microsite' || siteMenu === 'theSource')) {
                          // header3 on GOV doesn't change icons
                          if (!e.target.closest('header.header3') && !e.target.closest('header.header2')) {
                              let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#plus');
                              toggleIcon.setAttribute('href', iconHref);
                          }
                      }

                      if (toggleIcon && siteMenu == 'gov') {
                          // header3 on GOV doesn't change icons
                          if (!e.target.closest('header.header3') && !e.target.closest('header.header2')) {
                              let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#chevron-down');
                              toggleIcon.setAttribute('href', iconHref);
                          }
                      }
                  }
              }
          }

          function closeAllMobileSubmenus(e) {            
              if (!e.currentTarget.classList.contains('open')) {
                  const openNodes = document.querySelectorAll('.main-menu .node.open');
                  const openChildNodes = document.querySelectorAll('.main-menu .child-node.open');
                  
                  if (openNodes) {
                      openNodes.forEach((node) => {
                          const toggleIcon = node.querySelector('.parent .parent-toggle svg use');

                          node.classList.remove('open');
                          // Microsites & GOV mobile menu uses + and - icons
                          if (toggleIcon && (siteMenu === 'microsite' || siteMenu === 'theSource')) {
                              // header3 on GOV doesn't change icons
                              if (!e.target.closest('header.header3') && !e.target.closest('header.header2')) {
                                  let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#plus');
                                  toggleIcon.setAttribute('href', iconHref);
                              }
                          }

                          if (toggleIcon && siteMenu == 'gov') {
                              // header3 on GOV doesn't change icons
                              if (!e.target.closest('header.header3') && !e.target.closest('header.header2')) {
                                  let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#chevron-down');
                                  toggleIcon.setAttribute('href', iconHref);
                              }
                          }
                      });
                  }
                  
                  if (openChildNodes) {
                      openChildNodes.forEach((node) => {
                          const toggleIcon = node.querySelector('.children .child-toggle svg use');

                          node.classList.remove('open');
                          // Microsites & GOV mobile menu uses + and - icons
                          if (toggleIcon && (siteMenu === 'microsite' || siteMenu === 'theSource')) {
                              // header3 on GOV doesn't change icons
                              if (!e.target.closest('header.header3') && !e.target.closest('header.header2')) {
                                  let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#plus');
                                  toggleIcon.setAttribute('href', iconHref);
                              }
                          }

                          if (toggleIcon && siteMenu == 'gov') {
                              // header3 on GOV doesn't change icons
                              if (!e.target.closest('header.header3') && !e.target.closest('header.header2')) {
                                  let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#chevron-down');
                                  toggleIcon.setAttribute('href', iconHref);
                              }
                          }
                      });
                  }
              }
          }

          // move search from desktop position to mobile position, depending on breakpoint
          function moveSearch() {

              // DOE 
              const headerSearchCol = document.querySelector('.doe-site-header .search-col');            
              const bannerTitleCol = document.querySelector('.doe-site-header .banner-title-col');
              const mainMenu = document.querySelector('.doe-site-header .main-menu');

              if (headerSearchCol && bannerTitleCol && mainMenu) {
                  if (window.innerWidth > _BREAKPOINTS_.lg) {
                      bannerTitleCol.after(headerSearchCol);
                  }
                  else {
                      mainMenu.prepend(headerSearchCol);
                  }
              }
          }

          function reformatRequired() {
              const forms = document.querySelectorAll('[data-sf-role="form-container"]');

              forms.forEach((form) => {
                  var labels = form.querySelectorAll('label');

                  labels.forEach((label) => {
                      let labelText = label.innerHTML;
                      if (labelText.indexOf('<span class="required">(Required)</span>') < 0)
                      {
                          labelText = labelText.replace('(Required)', '<span class="required">(Required)</span>');
                          label.innerHTML = labelText;
                      }
                  });

                  labels.forEach((label) => {
                      let labelText = label.innerHTML;
                      if (labelText.indexOf('<span class="required">(Required if not a U.S. Citizen)</span>') < 0)
                      {
                          labelText = labelText.replace('(Required if not a U.S. Citizen)', '<span class="required">(Required if not a U.S. Citizen)</span>');
                          label.innerHTML = labelText;
                      }
                  });

              });
          }

          function noMenu() {
              // remove menu and mobile toggle since no menu exists (DOE)
              const mainMenu = document.querySelector('.main-menu');
              const mobileToggleBtn = document.querySelector('.mobile-nav-toggle');

              if (mainMenu) {
                  mainMenu.remove();
              }
              if (mobileToggleBtn) {
                  mobileToggleBtn.remove();
              }
          }
      }
  }

  class NotificationBanner {
      static main() {
          const notifications = document.querySelectorAll('.notification-banner');
          const isMobile = window.innerWidth < 1200;
          const hasLeftNav = document.querySelector('.left-nav-desktop-wrapper');

          notifications.forEach((notification) => {
              const closeBtn = notification.querySelector('.js-close-notification');

              if (closeBtn) {
                  closeBtn.addEventListener('click', (e) => { closeNotification(e, notification); });
              }
          });

          //logic to add bottom margin to mobile view header if no left nav is present
          if (isMobile && !hasLeftNav) {
              const subForm = document.querySelector('.searchbar-sub-form');
              
              if (subForm) {
                  subForm.style.marginBottom = "16px";
              }
              else {
                  const titleBar = document.querySelector('.page-title-bar-brand');
                  if (titleBar) {
                      titleBar.style.marginBottom = "16px";
                  }
              }
          }


          function closeNotification(e, notification) {
              e.preventDefault();
              notification.classList.add('hide');
          }
      }
  }

  class ParallaxHero {
      static main() {
          const heros = document.querySelectorAll('.parallax-hero');
          let useImg = "";

          heros.forEach(function (hero) {
              if (hero) {
                  init(hero);
              }
          });

          function init(hero) {
              setTimeout(function () {
                  setBG(hero);
              }, 500);
              window.addEventListener('resize', () => setBG(hero));
          }

          // determines if on desktop or mobile and changes image accordingly
          function setBG(hero) {
              const desktopImg = hero.getAttribute('data-desktop-img');
              const mobileImg = hero.getAttribute('data-mobile-img');
              const currentImg = hero.style.backgroundImage;

              if (desktopImg && hero) {
                  useImg = (!mobileImg || window.innerWidth >= _BREAKPOINTS_.sm) ? desktopImg : mobileImg;

                  if (currentImg == '' || !useImg.includes(currentImg)) {
                      hero.style.backgroundImage = "url('" + useImg + "')";
                  }
              }

          }
      }
  }

  class ParallaxImageTextOverlay {
      static main() {

          const fullWidthCtas = document.querySelectorAll('.parallax-image-text-overlay');
          let useImg = "";

          if (fullWidthCtas) {
              fullWidthCtas.forEach((banner) => {
                  init(banner);
              });
          }
          function init(banner) {
              setTimeout(function() {                
                  setBG(banner);
              }, 500);
              window.addEventListener('resize', (e) => { setBG(banner); });
          }

          // determines if on desktop or mobile and changes image accordingly
          function setBG(banner) {
              const desktopImg = banner.getAttribute('data-desktop-img');
              const mobileImg = banner.getAttribute('data-mobile-img');
              const currentImg = banner.style.backgroundImage;
              
              useImg = (!mobileImg || window.innerWidth >= _BREAKPOINTS_.lg) ? desktopImg : mobileImg;
              if (currentImg == '' || !useImg.includes(currentImg)) {
                  banner.style.backgroundImage = "url('"+useImg+"')";
              }
          }
      }
  }

  /**
   * Animation Helpers
   */

  const fadeOut = (element) => {
      element.style.opacity = 1;
      (function fade() {
          if ((element.style.opacity -= 0.1) < 0) {
              element.style.display = 'none';
          } else {
              requestAnimationFrame(fade);
          }
      })();
  };

  const fadeIn = (element, display) => {
      element.style.opacity = 0;
      element.style.display = display || "block";
      (function fade() {
          let val = parseFloat(element.style.opacity);
          if ((val += 0.1) <= 1) {
              element.style.opacity = val;
              requestAnimationFrame(fade);
          }
      })();
  };

  class ScrollToTop {
      static main() {
  		
  		const btn = document.querySelector('.btn-scroll-to-top');
  		let isShown = false;
  		
  		if (btn) {
  			init();
  		}
          
  		function init() {
  	
  			window.addEventListener('scroll', () => {
  				if (window.scrollY > 350 && !isShown) {
  					fadeIn(btn);
  					isShown = true;
  				} else if (window.scrollY <= 350 && isShown) {
  					fadeOut(btn);
  					isShown = false;
  				}
  			});
  	
  			btn.onclick = (e) => {
  				e.preventDefault();
  				window.scrollTo({top: 0, behavior: 'smooth'});
  			};
  		}		
  	}
  }

  class SoftwareTools {
      static main() {

          const dropdownToggles = document.querySelectorAll('.dropdown-toggle');
          const ossdTable = document.querySelector('#ossd_table');
          
          function init() {
              // Table Filter Dropdowns
              dropdownToggles.forEach((toggle) => {
                  toggle.addEventListener('click', toggleDropdown);
              });

              window.addEventListener('click', clickedOutside);
              
              // Table
              OSSDDT.initDatatable('#ossd_table');
              
              $(document).on('change', '#dt_custom_filters .dropdown-menu input[type=checkbox]', function(e){
                  OSSDDT.getDatatable().draw();
              });
              $(document).on('click', '#dt_custom_filters .dropdown-menu', function(e){
                  if($(e.target).not('input[type=checkbox]')) $(e.target).find('input[type=checkbox]').click();
                  e.stopPropagation();
              });
              $(document).on('click', '#sortbydropdown a', function(e) {
                  e.preventDefault();
                  var va=$(this).data('sortval').split(' ');
                  OSSDDT.getDatatable().order([[va[0], va[1]]]).draw();
                  $('#sortbydropdown-btn-label').text($(this).text());
              });            
          }

          // Toggle filter dropdowns
          function toggleDropdown(e) {
              e.preventDefault();
              const dropdownMenu = e.currentTarget.nextElementSibling;
              if (!dropdownMenu || !dropdownMenu.classList.contains('dropdown-menu'))
                  return;
              
              // close any open ones first
              dropdownToggles.forEach((toggle) => {
                  if (toggle != e.currentTarget) {
                      const dropdown = toggle.nextElementSibling;
                      
                      if (!dropdownMenu.classList.contains('dropdown-menu'))
                          return;

                      toggle.setAttribute('aria-expanded', false);
                      dropdown.classList.remove('show');
                  }
              });

              // toggle clicked one
              if (e.currentTarget.getAttribute('aria-expanded') != "true") {                
                  e.currentTarget.setAttribute('aria-expanded', true);
                  dropdownMenu.classList.add('show');
              }
              else {
                  dropdownMenu.classList.remove('show');
                  e.currentTarget.setAttribute('aria-expanded', false);
              }
          }

          // check if clicked outside of dropdown, if so close all dropdowns
          function clickedOutside(e) {         
              const withinDropdown = e.target.closest('.dropdown');   
              
              if (!withinDropdown) {                
                  // close any open ones first
                  dropdownToggles.forEach((toggle) => {
                      const dropdownMenu = toggle.nextElementSibling;
                      
                      if (!dropdownMenu.classList.contains('dropdown-menu'))
                          return;

                      toggle.setAttribute('aria-expanded', false);
                      dropdownMenu.classList.remove('show');
                      
                  });
              }
          }

          // DataTables for OSSDDT table
          // @TODO:  Come back once we get api data from client to have all data.  JS errors at the moment, possilbiy due to data pulled from prod but incomplete
         
          var OSSDDT=function(){
              var dt, methods={};
              methods.getDatatable=function(){
                  return dt;
              };
              methods.initDatatable=function(dtid){                
                  // Create the datatable
                  dt=$(dtid).DataTable({
                      destroy: true,
                      dom: 'itpr',
                      columns: [
                          null, {'visible' : false }, {'visible' : false }, {'visible' : false }, {'visible' : false }, {'visible' : false }, {'visible' : false }, {'visible' : false }
                      ],
                      drawCallback: function(settings) {
                          $(dtid+' thead').remove();
                          var api=this.api();
                          var data=api.rows({page: 'current'}).data();
                          $.each(data, function(i) {
                              var contents='<h2 class="h4 mb-0">';
                              if(data[i][1] != '') contents += ('<a href="' + data[i][1] + '">');
                              contents += data[i][2];
                              if(data[i][1] != '') contents += '</a>';
                              contents += ('</h2><div>Release Date: '+((data[i][5]['display'] == '') ? 'n/a' : data[i][5]['display'])+'</div><div class="mt-3">'+data[i][3]+'</div>');
                              $(dtid+' tbody > tr:eq('+i+') > td:eq(0)').html(contents);
                          });
                          api.rows({page: 'current'}).invalidate();
                      },
                      order: [[2, 'asc']],
                      pageLength: 30
                  });

                  // Add the LANGUAGE filter to the search chain
                  $.fn.dataTable.ext.search.push(
                  function(settings, data, dataIndex){
                      var fv=[];
                      $('#dtf-language').closest('.dropdown').find('input[type=checkbox]').each(function(){
                          if($(this).is(':checked')) fv.push($(this).val());
                      });
                      $('#dtf-language-count').text(fv.length);
                      if(fv.length === 0) return true;
                          var rv=dt.row(dataIndex).data()[4];
                      if(rv === '') return false;
                          rv=rv.split(';').map(function(t){return $.trim(t);});
                      return ($(rv).filter(fv).length > 0);
                  }
                  );
                  // Add the TECHNOLOGY filter to the search chain
                  $.fn.dataTable.ext.search.push(
                  function(settings, data, dataIndex){
                      var fv=[];
                      $('#dtf-technology').closest('.dropdown').find('input[type=checkbox]').each(function(){
                          if($(this).is(':checked')) fv.push($(this).val());
                      });
                      $('#dtf-technology-count').text(fv.length);
                      if(fv.length === 0) return true;
                          var rv=dt.row(dataIndex).data()[7];
                      if(rv === '') return false;
                          rv=rv.split(';').map(function(t){return $.trim(t);});
                      return ($(rv).filter(fv).length > 0);
                  }
                  );
                  // Add the YEAR filter to the search chain
                  $.fn.dataTable.ext.search.push(
                  function(settings, data, dataIndex){
                      var fv=[];
                      $('#dtf-year').closest('.dropdown').find('input[type=checkbox]').each(function(){
                          if($(this).is(':checked')) fv.push($(this).val());
                      });
                      $('#dtf-year-count').text(fv.length);
                      if(fv.length === 0) return true;
                          var rv=dt.row(dataIndex).data()[6];
                      if(rv === '') return false;
                          rv=('20'+rv.split('/').pop());
                      return ($.inArray(rv, fv) > -1);
                  }
                  );
                  $('#dtf-filter').on('keyup search input paste cut', function(e){
                      dt.search(this.value).draw();
                  });
              };
              return methods;
          }();

      
          if (ossdTable) {
              init();
          }  

      }
      
  }

  class TeamMembers {
      static main() {
          const teamMemberSections = document.querySelectorAll('.team-members');
          
          if (teamMemberSections) {
              teamMemberSections.forEach((teamMembers) => {
                  init(teamMembers);
              });
          }
          
          function init(teamMembers) {            
              const cards = teamMembers.querySelectorAll('.team-card').length;
              const loadMoreBtn = teamMembers.querySelector('.load-more-members-btn');
              const searchBtn = teamMembers.querySelector('#keyword-search');

              if (cards && cards <= 4) { 
                  teamMembers.classList.remove('fade-active');
              }

              if (loadMoreBtn) {
                  loadMoreBtn.addEventListener('click', (e) => { expandModule(e, teamMembers); });
              }

              if (searchBtn) {
                  searchBtn.addEventListener('click', (e) => { teamSearchRedirect(e, teamMembers); });
              }
          }

          function teamSearchRedirect(e, teamMembers) {
              e.preventDefault();

              const searchTerm = teamMembers.querySelector('#keyword-input');

              if (searchTerm.length < 3) {
                  alert("Please enter at least 3 letters to search for");
              }
              else {
                  window.location.href = '?search=' + searchTerm.value;
              }
          }

          function expandModule(e, teamMembers) {
              e.preventDefault();
              
              if (!e.currentTarget.classList.contains('expanded')) {
                  teamMembers.classList.add('expanded');
                  e.currentTarget.classList.add('expanded');
                  e.currentTarget.querySelector('.text').innerHTML = "Show Less";
                  const elementScrollHeight = teamMembers.scrollHeight;
                  // console.log('elementScrollHeight: ', elementScrollHeight);
                  
                  teamMembers.style.maxHeight = elementScrollHeight.toString() + 'px';
              }
              else {
                  teamMembers.classList.remove('expanded');
                  e.currentTarget.classList.remove('expanded');
                  e.currentTarget.querySelector('.text').innerHTML = "Load More";
                  
                  if (window.innerWidth > 991.8) {
                      teamMembers.style.maxHeight ='842px';
                  } else {
                      teamMembers.style.maxHeight ='1500px';
              
                      teamMembers.scrollIntoView();
                  }
              }
          }
      }
  }

  class ToggleSearch {
      static main() {

          const toggleSearchBtns = document.querySelectorAll('.toggle-search');
          const body = document.querySelector('body');
          const srcSubSearch = document.querySelector(".searchbar-sub-form");
          const externalSubSearchBtn = document.querySelector('.external-sub-search-button');
          const externalSubSearchBox = document.querySelector(".external-sub-search-input");  
          const externalSearchBtns = document.querySelectorAll('.external-search-button');
          

          for (let x=0; x< externalSearchBtns.length; x++)
          {
              if (externalSearchBtns[x])
              {
                  externalSearchBtns[x].addEventListener('click', redirectToExternalSearch);   

                  if (externalSearchBtns[x].previousElementSibling)
                  {
                      externalSearchBtns[x].previousElementSibling.addEventListener("keypress", function(event) {
                          // If the user presses the "Enter" key on the keyboard
                          if (event.key === "Enter") {
                              // Cancel the default action, if needed
                              event.preventDefault();
                              // Trigger the button element with a click
                              externalSearchBtns[x].click();
                          }                    });
                  }

                  if (externalSearchBtns[x].parentElement.parentElement.childNodes[1].childNodes[3])
                  {
                      externalSearchBtns[x].parentElement.parentElement.childNodes[1].childNodes[3].addEventListener("keypress", function(event) {
                          // If the user presses the "Enter" key on the keyboard
                          if (event.key === "Enter") {
                              // Cancel the default action, if needed
                              event.preventDefault();
                              // Trigger the button element with a click
                              externalSearchBtns[x].click();
                          }                    });
                  }
              }
          }
          
          for (let x=0; x< toggleSearchBtns.length; x++)
          {     
              toggleSearchBtns[x].addEventListener('click', toggleSearch);
          }

          if (srcSubSearch) {
              const pageTitle = document.querySelector('.page-title-bar-brand');

              if (pageTitle) {
                  pageTitle.classList.add('no-margin');
              }
          }

          if (externalSubSearchBtn)
          {
              externalSubSearchBtn.addEventListener('click', redirectToExternalSubSearch);   

              externalSubSearchBox.addEventListener("keypress", function(event) {
                  // If the user presses the "Enter" key on the keyboard
                  if (event.key === "Enter") {
                    // Cancel the default action, if needed
                    event.preventDefault();
                    // Trigger the button element with a click
                    externalSubSearchBtn.click();
                  }            });
          }

          function toggleSearch(e) {
              e.preventDefault();

              // close any open menus 
              const openNodes = document.querySelectorAll('.main-menu .node.open');
              const openChildNodes = document.querySelectorAll('.main-menu .child-node.open');
              
              if (openNodes) {
                  openNodes.forEach((node) => {
                      const toggleIcon = node.querySelector('.parent .parent-toggle svg use');

                      node.classList.remove('open');
                      if (toggleIcon) {
                          let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#plus');
                          toggleIcon.setAttribute('href', iconHref);
                      }
                  });
              }
              
              if (openChildNodes) {
                  openChildNodes.forEach((node) => {
                      const toggleIcon = node.querySelector('.children .child-toggle svg use');

                      node.classList.remove('open');
                      if (toggleIcon) {
                          let iconHref = toggleIcon.getAttribute('href').replace(/\#.*/, '#plus');
                          toggleIcon.setAttribute('href', iconHref);
                      }
                  });
              }

              // open search header2
              const header3ToggleSearchBtn = document.querySelector('.header3 .toggle-header3-search.toggle-search');
              const headerTwoToggleSearchBtn = document.querySelector('.header2 .toggle-header3-search');
              const headerOneToggleSearchBtn = document.querySelector('.header1 .toggle-search');

              if (!body.classList.contains('search-open') && headerTwoToggleSearchBtn || !body.classList.contains('search-open') && header3ToggleSearchBtn || !body.classList.contains('search-open') && headerOneToggleSearchBtn) {
                  const mobileSearch = document.querySelector('.mobile-search-menu-wrapper');
                  const searchMenuWrap = document.querySelector('.search-menu-wrapper');
                  const mobileSearchSpacer = document.querySelector('.mobile-search-spacer');
                  const headerOneSearch = document.querySelector('.header1 .header-search');
                  const headerOneToggleSearchBtn = document.querySelector('.header1 .toggle-search');
                  const pageWrapper = document.querySelector('.page-wrapper');
                  const searchButton = document.querySelector('.search-alternate');


                  body.classList.remove('menu-open');
                  body.classList.add('search-open');
                  if (searchButton) {
                      searchButton.style.display = "block";
                  }

                  if (mobileSearchSpacer) {
                      mobileSearchSpacer.style.display = "none";
                  }


                  if(mobileSearch) {
                      mobileSearch.classList.remove('hide');
                      headerTwoToggleSearchBtn.classList.add('show');
                  }
                  if(searchMenuWrap && header3ToggleSearchBtn) {
                      searchMenuWrap.classList.remove('hide');
                      header3ToggleSearchBtn.classList.add('show');
                      if (window.innerWidth <= 992 && mobileSearchSpacer)
                      {
                          mobileSearchSpacer.style.display = "block";
                      }
                      pageWrapper.style.marginTop = '60px';
                  }
                  if(searchMenuWrap && headerOneToggleSearchBtn) {
                      body.classList.add('search-open');
                      searchMenuWrap.classList.remove('hide');
                      headerOneSearch.style.display = 'block';
                  }
              }
              else {
                  const mobileSearch = document.querySelector('.mobile-search-menu-wrapper');
                  const searchMenuWrap = document.querySelector('.search-menu-wrapper');
                  const mobileSearchSpacer = document.querySelector('.mobile-search-spacer');
                  const headerOneSearch = document.querySelector('.header1 .header-search');
                  const pageWrapper = document.querySelector('.page-wrapper');
                  const searchButton = document.querySelector('.search-alternate');

                  for (let x=0; x< toggleSearchBtns.length; x++)
                  {     
                      toggleSearchBtns[x].classList.remove('show');
                  }

                  body.classList.remove('search-open');

                  if (searchButton) {
                      searchButton.style.display = "none";
                  }

                  if(mobileSearch) {
                      mobileSearch.classList.add('hide');
                      headerTwoToggleSearchBtn.classList.remove('show');
                  }
                  if(searchMenuWrap && header3ToggleSearchBtn) {
                      searchMenuWrap.classList.add('hide');
                      header3ToggleSearchBtn.classList.remove('show');
                      if (mobileSearchSpacer){
                          mobileSearchSpacer.style.display = "none";
                      }
                      pageWrapper.style.marginTop = '20px';
                  }
                  if(searchMenuWrap && headerOneToggleSearchBtn) {
                      body.classList.remove('search-open');
                      searchMenuWrap.classList.add('hide');
                      headerOneSearch.style.display = 'none';
                  }
              }
              
          }

          function redirectToExternalSearch(e) {
              let externalTarget = e.target.getAttribute("data-href");
              let searchTerm = e.target.parentNode.childNodes[1]?.value ?? "";
              if (!searchTerm || searchTerm == "") {
                  searchTerm = e.target.parentNode.childNodes[5]?.value ?? "";
                  if (!searchTerm || searchTerm == "")
                  {
                      searchTerm = e.target.parentNode.parentNode.childNodes[1].childNodes[3]?.value ?? "";
                  }
              }

              externalTarget = externalTarget.replace("{query}", encodeURIComponent(searchTerm.replace(/<\/?[^>]+(>|$)/g, "")));

              window.location.href = externalTarget;
          }

          function redirectToExternalSubSearch(e) {
              let externalTarget = externalSubSearchBtn.getAttribute("data-href");
              const searchTerm = externalSubSearchBox.value;
              externalTarget = externalTarget.replace("{query}", encodeURIComponent(searchTerm.replace(/<\/?[^>]+(>|$)/g, "")));

              window.location.href = externalTarget;
          }
      }
  }

  class videoPopup {
      static main() {
          const videos = document.querySelectorAll('.video-popup');
          
          if (videos.length) {
              init();
          }
          
          function init() {
              videos.forEach((video) => {
                  const openModalBtn = video.querySelector('.js-open-modal');
                  openModalBtn.addEventListener('click', (e) => {generateModal(e, video); });

                  const openModalLink = video.querySelector('.js-open-modal-link');
                  if (openModalLink) {
                      openModalLink.addEventListener('click', (e) => {generateModal(e, video); });
                  }
              });
          }
          
          function generateModal(e, video) {
              e.preventDefault();
              const body = document.querySelector('body');
              video.querySelector('.js-open-modal');
              const id = video.getAttribute('data-video-id');
              const modalAlt = 'video modal for id: '+id+'';
              const iframeHref = video.getAttribute('data-video-url');
              const videoTitle = video.getAttribute('data-video-title');            
              
              Element.prototype.setAttributes = function(obj){
                  for(var prop in obj) {
                      this.setAttribute(prop, obj[prop]);
                  }
              };
              
              // create modal to be added at end of body tag
              const modalDiv = document.createElement("div");
              modalDiv.setAttributes({
                  id: "video-modal-"+id,
                  "data-clicked-id": id,
                  class: 'video-modal',
                  "aria-label": modalAlt,
                  role: "dialog",
                  "aria-modal": true,
                  "aria-live": "polite"
              });

              // create modal content with iframe
              const modalContent = `
                <div class="modal-overlay">
                    <div class="modal-inner">
                        <header class="modal-header">
                            <a href="#" class="js-close-modal" aria-label="click to close video">
                                <svg><use href="/ResourcePackages/Frontend/public/library/img/icon/sheets/icons.svg#close"></use></svg>
                            </a>
                            <h2 class="video-title">${videoTitle}</h2>
                        </header>
                        <div class="modal-body">
                            <div class="video-wrapper">
                                <iframe src="${iframeHref}" width="640" height="400" title="${videoTitle}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
                            </div>                           
                        </div>
                        <footer class="modal-footer">
                            <a href="#" class="js-close-modal btn btn-primary" aria-label="Click to close video">Close</a>
                        </footer>        
                    </div>
                </div>
            `;
              
              // add modal to body
              modalDiv.innerHTML = modalContent;
              body.appendChild(modalDiv);

              fixTabbing();
              bindModal();
          }
          
          function bindModal() {
              // const closeBtns = document.querySelectorAll('.video-modal .js-close-btn');
              // const overlay = document.querySelector('.video-modal .modal-overlay');
              document.addEventListener('click', function(e) {
                  if (e.target.closest('.js-close-modal')) {
                      closeModal(e);

                      // stop youtube videos from playing when in a modal and you close modal
                      const modal = e.target.closest('.video-modal');
                      modal.addEventListener('hidden.bs.modal', function() {
                          var iframe = modal.querySelector('iframe');
                          iframe.src = iframe.src;
                      });
                  }
              });
          }

          function fixTabbing() {
              const links = document.querySelectorAll('.video-modal a');

              if (links) {
                  links[0].focus();

                  links.forEach((link) => {
                      link.addEventListener('focusout', function() {
                          if (link == links[links.length - 1]) {
                              console.log('last link');
                              links[0].focus();
                          }
                      });
                  });
                  
              }
          }
          function closeModal(e) {
              e.preventDefault();
              const videoModal = e.target.closest('.video-modal');
              const id = videoModal.getAttribute('data-clicked-id');
              
              videoModal.remove();
              const clickedVideo = document.getElementById(id);
              clickedVideo.querySelector('.video-poster').focus();
          }
      }
  }

  class VideoHero {
      static main() {        
          const heros = document.querySelectorAll('.video-hero');        
          
          heros.forEach((hero) => {
              const heroVideo = hero.querySelector('video');
              const pauseBtn = hero.querySelector('.controls .pause-video');
              const playBtn = hero.querySelector('.controls .play-video');

              if (pauseBtn) {
                  pauseBtn.addEventListener('click', () => {pauseVideo(heroVideo, pauseBtn, playBtn);});
              }
              if (playBtn) {
                  playBtn.addEventListener('click', () => {playVideo(heroVideo, pauseBtn, playBtn);});   
              }
          });
          
          function pauseVideo(heroVideo, pauseBtn, playBtn) {
              heroVideo.pause();
              pauseBtn.classList.add('hide');
              playBtn.classList.remove('hide');
          }
          
          function playVideo(heroVideo, pauseBtn, playBtn) {
              heroVideo.play();
              pauseBtn.classList.remove('hide');
              playBtn.classList.add('hide');
          }
      }
  }

  class TabbedContent {
      static main() {
          document.querySelectorAll(".tabbed-content").forEach((tabbedContent) => {
              const targetList = tabbedContent.querySelector(".nav-tabs");
              if (!targetList) return;
              const listItems = [...tabbedContent.querySelectorAll(".tab-pane")].map((tab, i) => {
                  if (i === 0) tab.classList.add("show", "active");
                  const li = document.createElement("li");
                  li.classList.add("nav-item");
                  li.setAttribute("role", "presentation");
                  const btn = document.createElement("button");
                  btn.classList.add("nav-link");
                  if (i === 0) {
                      btn.classList.add("active");
                      btn.setAttribute("aria-selected", true);
                  } else {
                      btn.setAttribute("aria-selected", false);
                  }
                  btn.id = tab.getAttribute("aria-labelledby");
                  btn.dataset.bsToggle = "tab";
                  btn.dataset.bsTarget = "#" + tab.id;
                  btn.type = "button";
                  btn.role = "tab";
                  btn.setAttribute("aria-controls", tab.id);
                  btn.textContent = tab.dataset.heading;
                  li.append(btn);
                  return li;
              });
              targetList.replaceChildren(...listItems);
          });
      }
  }

  class MailChimpForm {
      static main() {       

          function setItemWithDate(key, value, newDate) {
              const data = {
                value: value,
                date: newDate ?? new Date()
              };
              localStorage.setItem(key, JSON.stringify(data));
            }
            
            // Getting the variable and its creation date
            function getItemWithDate(key) {
              const data = JSON.parse(localStorage.getItem(key));
              if (data) {
                return { value: data.value, date: new Date(data.date) };
              } else {
                return null;
              }
            }

          let subscribeForm = document.getElementById('mc-embedded-subscribe-form');
          
          //check element exists before we do anything with jquery to avoid script errors
          if (subscribeForm)
          {
              $(subscribeForm).validate();

              var isPopup = document.getElementById('hidSecondsToPopup');
              if (isPopup)
              {
                  let seconds = $('#hidSecondsToPopup').val();
                  seconds = isNaN(seconds) ? 0 : parseInt(seconds);

                  let start = $('#hidVisitStart').val();
                  start = isNaN(start) ? 0 : parseInt(start);

                  let end = $('#hidVisitEnd').val();
                  end = isNaN(end) ? 1000 : parseInt(end);

                  const formCode = $('#hidFormCode').val();

                  if (formCode && (seconds || start || end)) {
                      const result = getItemWithDate("form_" + formCode);

                      let visitNo = (!result) || isNaN(result?.value) ? 0 : parseInt(result.value);
                      let originalDate = result?.date;

                      //check if a year has elapsed - if so, reset
                      if (originalDate) {

                          var diffDays = parseInt((new Date() - originalDate) / (1000 * 60 * 60 * 24), 10); 
                          if (diffDays > 365) {
                              originalDate = new Date();
                              visitNo = 0;
                          }
                      }

                      visitNo = visitNo + 1;

                      setItemWithDate("form_" + formCode, visitNo, originalDate);

                      if ((start <= visitNo) && (end > visitNo)) {
                          setTimeout(function () {
                              $('#popup-subscribe').modal('show');
                          }, seconds * 1000);
                      }
                  }
              }
          }
      }
  }

  class GovNews {
      static main() {

          const newsListing = document.querySelector('.news-listing');
          const bodyClass = document.body.classList;

          const searchInput =  document.getElementById('news-search-input');
          const yearFilter = document.getElementById('filter-by-year');
          const sortBy = document.getElementById('sort-by');
          const newsType = document.getElementById('news-type');
          const newsTopic = document.getElementById('news-topic');
          const searchButton = document.getElementById('news-search-button');
          const clearButton = document.getElementById('clear-search-button');

          if (newsListing && bodyClass.contains('gov'))
          {
              setEvents();
              setData();
          }

          function setEvents() {
              if (searchInput)
              {
                  searchInput
                      .addEventListener("keyup", function (event) {
                          event.preventDefault();
                          if (event.keyCode === 13) {
                              document.getElementById("news-search-button").click();
                          }
                      });
              }
              
              if (yearFilter)
              {
                  yearFilter
                      .addEventListener("change", function (event) {
                          event.preventDefault();
                          DoSearch();
                      });
              }

              if (newsType)
              {
                  newsType
                      .addEventListener("change", function (event) {
                          event.preventDefault();
                          DoSearch();
                      });
              }

              if (newsTopic)
              {
                  newsTopic
                      .addEventListener("change", function (event) {
                          event.preventDefault();
                          DoSearch();
                      });
              }
              
              if (sortBy)
              {
                  sortBy
                      .addEventListener("change", function (event) {
                          event.preventDefault();
                          DoSearch();
                      });
              }
          
              if (searchButton) {
                  searchButton
                      .addEventListener("click", function (event) {
                          event.preventDefault();
                          DoSearch();
                      });
              }
              
          if (clearButton)
              {
                  clearButton
                      .addEventListener("click", function (event) {
                          event.preventDefault();
                          ClearSearch();
                      });
              }
          }
          
          function setData() {
          
              const urlParams = new URLSearchParams(window.location.search);
              const selectedYear = urlParams.get('year');
              const selectedSort = urlParams.get('sort');
              const searchText = urlParams.get('search');
              const selectedType = urlParams.get('type');
              const selectedTopic = urlParams.get('topic');
          
              if (yearFilter)
              {
                  var yearsList = document.getElementById('hidYears');

                  if (yearsList)
                  {
                      var jsonYears = JSON.parse(yearsList.value);
                      for (var x = 0; x < jsonYears.length; x++)
                      {
                          const yearOption = document.createElement("option");
                          yearOption.setAttribute("value", jsonYears[x]);
                          yearOption.innerText = jsonYears[x];
                          if (yearOption.value == selectedYear) {
                              yearOption.selected = true;
                          }
                          yearFilter.appendChild(yearOption);
                      }
                  }
              }		
          
              if (sortBy)
              {
                  for (var i = 0; i < sortBy.length; i++) {
                      const sort = sortBy[i];
                      if (sort.value == selectedSort) {
                          sort.selected = true;
                      }
                  }
              }

              if (selectedType && newsType)
              {
                  for (var i = 0; i < newsType.length; i++) {
                      const type = newsType[i];
                      if (type.value == selectedType) {
                          type.selected = true;
                      }
                  }
              }

              if (selectedTopic && newsTopic)
              {
                  for (var i = 0; i < newsTopic.length; i++) {
                      const topic = newsTopic[i];
                      if (topic.value == selectedTopic) {
                          topic.selected = true;
                      }
                  }
              }
          
              if (searchInput)
              {
                  searchInput.value = searchText;
              }
          }
          
          
          function DoSearch(event) {
              var searchString = "";
              const urlParams = new URLSearchParams(window.location.search);
              const selectedYear = urlParams.get('year') ?? "";
              const selectedSort = urlParams.get('sort') ?? "" ;
              const selectedSearch = urlParams.get('search') ?? "";
              const selectedType = urlParams.get('type') ?? "";
              const selectedTopic = urlParams.get('topic') ?? "";
          
              if (searchInput)
              {
                  var search = searchInput.value ?? selectedSearch;
                  if (search && search !== "")
                      searchString += searchString + "&search=" + encodeURIComponent(search.replace(/<\/?[^>]+(>|$)/g, ""));
              }
          
              if (sortBy)
              {
                  var sort = sortBy.value ?? selectedSort;
                  if (sort && sort !== "")
                      searchString += "&sort=" + encodeURIComponent(sort.replace(/<\/?[^>]+(>|$)/g, ""));
              }

              if (newsType)
              {
                  var type = newsType.value ?? selectedType;
                  if (type && type !== "")
                      searchString += "&type=" + encodeURIComponent(type.replace(/<\/?[^>]+(>|$)/g, ""));
              }

              if (newsTopic)
              {
                  var topic = newsTopic.value ?? selectedTopic;
                  if (topic && topic !== "")
                      searchString += "&topic=" + encodeURIComponent(topic.replace(/<\/?[^>]+(>|$)/g, ""));
              }
              else
              {
                  if (selectedTopic && selectedTopic != '')
                  {
                      searchString += "&topic=" + encodeURIComponent(selectedTopic.replace(/<\/?[^>]+(>|$)/g, ""));
                  }
              }
                      
              if (yearFilter)
              {
                  var year = yearFilter.value ?? selectedYear;
                  if (year && year !== "")
                      searchString += "&year=" + encodeURIComponent(year.replace(/<\/?[^>]+(>|$)/g, ""));
              }

              let path = window.location.href.split('?')[0];
              
              var segments = path.split('/');

              if (isNumeric(segments[segments.length-1]))
                  segments.pop();

              var searchPath = segments.join('/');
              

              window.location.href = searchPath + "?ign=1" + searchString;
          }
          
          function ClearSearch() {
              const urlParams = new URLSearchParams(window.location.search);
              const selectedTopic = urlParams.get('topic');

              let path = window.location.href.split('?')[0];
          
              if (path.indexOf("-in-category") > 0) {
                  path = path.substring(0, path.indexOf("-in-category"));
              }

              if (!newsTopic && selectedTopic && selectedTopic != '')
              {
                  path=path + "?ign=1&topic=" + selectedTopic;
              }
              
              window.location.href = path;
          }

          function isNumeric(n) {
              return !isNaN(parseFloat(n)) && isFinite(n);
            }
      }

      
  }

  class ShareThisThreads {
      static main() {
  		
  		/*
      * Custom Threads button for ShareThis
      */
  		function updateThreadsLink() {
  			const currentUrl = window.location.href;
  			const currentTitle = document.title;
  			const encodedTitle = encodeURIComponent(currentTitle);
  			const encodedUrl = encodeURIComponent(currentUrl);
  			const updatedUrl = `https://www.threads.net/intent/post?text=${encodedTitle}%0A${encodedUrl}`;
  	
  			// Calculate the position to center the window
  			const screenWidth = window.screen.width;
  			const screenHeight = window.screen.height;
  			const windowWidth = 785;
  			const windowHeight = 450;
  			const left = (screenWidth - windowWidth) / 2;
  			const top = (screenHeight - windowHeight) / 2;
  	
  			// Open the link in a new window with specific size and position
  			const windowFeatures = `width=${windowWidth},height=${windowHeight},left=${left},top=${top}`;
  			window.open(updatedUrl, 'myWindow', windowFeatures);
  		}
  	
  		var threads = document.getElementById("btnthreads");
  		if (threads)
  		{
  			threads.addEventListener("click", updateThreadsLink);
  		}
  	}
  }

  /* Vendors */

  //JS Applications

  // //High Charts
  // import './components/GOV/apps/highCharts/BusinessVolume';
  // import './components/GOV/apps/highCharts/DemographicsCharts';
  // import './components/GOV/apps/highCharts/Energy';
  // import './components/GOV/apps/highCharts/Transportation';
  // import './components/GOV/apps/highCharts/StorageFutures1';
  // import './components/GOV/apps/highCharts/StorageFutures2';
  // import './components/GOV/apps/highCharts/StorageFutures3';
  // import './components/GOV/apps/highCharts/StorageFutures4';
  // import './components/GOV/apps/highCharts/StorageFutures5';
  // import './components/GOV/apps/highCharts/CheckTheStack1';
  // import './components/GOV/apps/highCharts/CheckTheStack2';

  //High Maps
  // import './components/GOV/apps/highMaps/StateSelect';

  // Shared
  if (typeof svg4everybody == 'function') {
  	svg4everybody();
  }

  // Site javascript
  Accordion.main();
  Alerts.main();
  AnimatedStat.main();
  BlogCards.main();
  Carousel.main();
  Datatables.main();
  FeaturedImageVideoCallout.main();
  Footer.main();
  heroBanner.main();
  IconCard.main();
  ImageCard.main();
  LeftNavMenu.main();
  LinkUpdates.main();
  LogosWidget.main();
  MainMenu.main();
  MailChimpForm.main();
  NewsCards.main();
  NotificationBanner.main();
  ParallaxHero.main();
  ParallaxImageTextOverlay.main();
  ScrollToTop.main();
  SoftwareTools.main();
  TabbedContent.main();
  TeamMembers.main();
  ToggleSearch.main();
  VideoHero.main();
  videoPopup.main();
  GovNews.main();
  ShareThisThreads.main();

  new ImageLoader()?.initialize();

})();
